import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { I18N_CONFIG } from './constants/i18nConfig';
import { advisorPortalEn, advisorPortalEs, advisorPortalFr, advisorPortalAr, advisorPortalZh } from './locales/advisorPortal';
import { lpWorkbenchEn, lpWorkbenchEs } from './locales/lpWorkbench';
import { participantEn, participantEs } from './locales/participantPortal';

const RTL_LANGS = new Set(['ar']);
const STORAGE_KEY = 'i18nextLng';

const resources = {
  en: {
    translation: {
      brandName: 'GLOBAL MOBILITY',
      loading: 'Loading...',
      signingIn: 'Signing you in...',
      logOut: 'Log out',
      back: 'Back',
      landing: {
        eyebrow: 'SPAIN · EXTRAORDINARY REGULARIZATION 2026',
        title1: 'Can you get',
        title2: 'legal',
        title3: 'residency',
        title4: 'in',
        titleYear: '2026?',
        intro1: "If you're already in Spain, this could be your chance to get legal residency. We will help you do it correctly.",
        intro2: 'The window closes <strong>June 30, 2026</strong>. If you wait - or make a mistake - you could lose this opportunity.',
        intro3: 'Work with us - done right the first time.',
        ctaMain: 'Check if I qualify',
        ctaSubline: '{{price}} all-inclusive · you only pay when submitted',
        ctaNote: 'No upfront payment · charged only when your application is submitted',
        attorneySection: 'YOUR ATTORNEY',
        attorneyRole: 'Immigration Attorney · Spain',
        attorneyStats: '10+ years experience · 1,500+ cases handled',
        attorneyHandle: '@travieso.global',
        attorneyFirmBadge: 'Travesio Immigration Law',
        available: 'Available',
        hideCreds: 'Hide credentials',
        viewCreds: 'View full credentials',
        bioP1: 'I help people who already live in Spain obtain legal residency in a clear, safe way without errors.',
        bioP2: 'My approach combines legal expertise with a structured review process to ensure applications are submitted complete and correctly on the first attempt.',
        bioP3: 'With more than <strong>10 years of experience</strong> in immigration law, I have handled over <strong>1,500 cases</strong>, guiding people throughout the process and helping them avoid rejections and administrative issues. I have also advised people through the immigration and international protection legal service of the Madrid Bar Association.',
        bioP4: 'I understand how important this opportunity is - and how easy it is to lose it because of a small mistake. That is why every case is reviewed in detail to ensure the application is complete, correct, and ready to submit.',
        quote: "I've handled more than 1,500 immigration cases in Spain. What I know is this: most denials are not due to eligibility - they are due to incorrect or incomplete documentation. That's exactly what we prevent. If you qualify, I want to make sure a paperwork error does not cost you your opportunity.",
        whyTitle: 'Why',
        whyAccent: 'Global Mobility',
        why1Title: 'A real person on your case',
        why1Desc: "You're assigned a dedicated specialist from day one who knows your file and is available when you need them.",
        why2Title: 'No upfront cost - pay only on success',
        why2Desc: '€250 fixed fee, only charged when your application is submitted.',
        why2Strong: '',
        why3Title: 'Lawyer-reviewed, every application',
        why3Desc: 'Before filing, a licensed immigration attorney will review every detail of your file.',
        why4Title: 'We handle everything for you',
        why4Desc1: "From first document to final submission -",
        why4Desc2: "you don't have to deal with the government portal.",
        why4Desc3: 'We do.',
        why5Title: 'Built to prevent mistakes',
        why5Desc: 'We combine real legal experience with a structured review process to identify and fix issues before submission.',
        howTitle: 'How it',
        howAccent: 'works',
        step1Title: 'Start (2 minutes)',
        step1Desc: 'A specialist will contact you on WhatsApp within 24 hours - no paperwork yet.',
        step2Title: 'Step-by-step guidance',
        step2Desc: 'A dedicated specialist works with you to collect and organize your documents.',
        step3Title: 'Legal review',
        step3Desc: 'A licensed immigration attorney reviews your full application <strong>before submission.</strong>',
        step4Title: 'Submission and follow-up',
        step4Desc: 'We submit your case and keep you informed until resolution.',
        pricingAria: 'Pricing and registration',
        pricingTitle: 'Pricing',
        allInclusive: 'ALL-INCLUSIVE',
        checkingOffer: 'Checking offer...',
        perPerson: 'Per person',
        noUpfront: 'No upfront payment - not a euro before submission',
        successFee: 'Only charged when your application is complete and submitted. No surprises.',
        whatIncludes: 'What is included',
        priceList1: 'Dedicated specialist from day one',
        priceList2: 'Full attorney review before submission',
        priceList3: 'Updates until final decision',
        partner: 'Partner: {{name}}',
        ctaBoxTitle: "Don't miss your opportunity to become legal in Spain.",
        ctaBoxDesc: 'The window is limited - and mistakes can cost you your chance.',
        ctaBoxButton: "Check if I qualify - it's free",
        ctaFreeNote: "It's free - no commitment",
        ctaLegal: 'Your information is protected and used only to manage your application. By registering, you agree to our Terms and Privacy Policy.',
        footer: {
          companyLine1: 'Global Mobility is a trade name of',
          companyName: 'Global Mobility Group LLC',
          companyAddress: '140 E Broadway Ave, Suite 25, Jackson, WY 83001, USA',
          companyLine2: 'EU Representative: Ana Maria Travieso Hernandez -',
          linkTerms: 'Terms of Service',
          linkPrivacy: 'Privacy Policy',
          linkCookies: 'Cookie Policy',
          linkLegal: 'Legal Notice',
          copyright: '© 2026 Global Mobility Group LLC',
        },
      },
      signup: {
        signIn: 'SIGN IN',
        newAccount: 'New Account',
        mobileTitle: 'First, enter<br />your <em>mobile number</em>',
        mobileTitleSignIn: 'Enter your <em>mobile</em> to sign in',
        mobileDesc:
          'Your information is used only to manage your application and is protected by our <privacy>Privacy Policy</privacy>.',
        continue: 'Continue',
        or: 'or',
        secondTitle: 'Second, do you have <em>WhatsApp?</em>',
        secondDesc: "Choose how you'd like to receive updates, documents, and messages from your specialist.",
        whatsappDesc: 'Receive documents and updates directly in WhatsApp.',
        smsTitle: 'Mobile (SMS)',
        smsDesc: 'Standard text messages. Works without internet.',
        tip: 'You can change your preferred channel at any time from your account settings.',
        sending: 'Sending...',
        otpTitle: 'Enter the code we sent',
        otpDesc: 'We sent a one-time code to your {{channel}}.',
        otpPlaceholder: 'Enter 6-digit code',
        verifying: 'Verifying...',
        otpResendTimerPrefix: 'Resend in ',
        otpResendReady: 'You can resend the code now.',
        otpResendCode: 'Resend code',
        terms: 'By continuing, you agree to our <terms>Terms of Service</terms>, <privacy>Privacy Policy</privacy>, and <dpa>Data Processing Agreement</dpa>.',
        noAccount: "Don't have an account?",
        signUpCta: 'Sign up →',
        haveAccount: 'Already have an account?',
        signInCta: 'Sign in →',
        errors: {
          invalidPhone: 'Please enter a valid phone number.',
          numberAlreadyRegistered: 'This number is already registered. Please sign in.',
          enterCode: 'Please enter the code you received.',
          invalidResponse: 'Invalid response. Please try again.',
          invalidOtp: 'Invalid or expired code.',
          sendFailed: 'Failed to send code. Try again.',
          requestTimeout: 'The server is taking too long to respond. Please try again.',
          checkPhoneFailed: 'Could not verify your number. Please try again.',
          ssoGeneric: 'Sign-in did not complete. Please try again.',
          emailExists: 'This email is already registered. Please sign in instead.',
          accessDenied: 'Sign-in was cancelled or access was denied. Please try again.',
          sessionFailed: 'Session could not be established. Please try again.',
          tokenExchangeFailed: 'Could not complete sign-in. Please try again.',
          userNotRegistered: 'This account is not registered. Please sign up first.',
          phoneLoginNotRegistered: 'User not registered. Please create an account first.',
          invalidRequest: 'Invalid sign-in request. Please try again.',
          serverError: 'Something went wrong on our end. Please try again later.',
        },
      },
      qualify: {
        eyebrow: 'Eligibility Check',
        badge: 'Takes less than 1 min',
        title: 'Check if you <accent>qualify</accent>',
        subtitle: "Answer a few yes/no questions to find out if you are eligible for Spain's 2026 Extraordinary Regularization",
        yes: 'Yes',
        no: 'No',
        altTitle: "Let's explore <accent>other options</accent>",
        altDesc: 'Based on your answers, you may not qualify for the 2026 Extraordinary Regularization. However, there may be other legal options available to you. Continue to register and a specialist will review your case to identify the best path forward.',
        continue: 'Continue',
        startOver: 'Start over',
        privacy: 'Your answers are confidential and protected by our Privacy Policy.',
        q1: 'Did you arrive in Spain before January 1, 2026?',
        q2: 'Have you ever applied for asylum in Spain?',
        q3: 'Did you apply for asylum in 2026?',
        q4: 'Has your asylum application been granted?',
        q5: 'Have you been convicted of a crime in Spain?',
        qualifiedBadge: 'Eligibility confirmed',
        qualifiedTitle: 'Great news - you qualify for the <accent>2026 Extraordinary Regularization.</accent>',
        qualifiedSub: "Let's move forward. Our team will guide you through every step of your application - from document review to final submission.",
        qualifiedPrivacy:
          'Your answers are confidential and protected by our <privacy>Privacy Policy</privacy>.',
      },
      successPage: {
        brandLogo: 'G',
        brandTitle: 'Global Mobility',
        brandSubtitle: 'Spain Regularization 2026',
        completedBadge: 'Registration complete',
        title: 'All done!',
        body: "Thank you for choosing me. To get started right away, I've set up a secure client portal where you can complete everything at your own pace and upload documents from your phone. This speeds up your case and reduces errors. Click below to begin.",
        next1: 'We review your information',
        next2: 'We contact you by phone or email',
        next3: 'We guide you through the next steps',
        next4: 'You can start preparing: passport, proof of time in Spain, and supporting documents',
        referral: {
          ariaLabel: 'Have a referral code?',
          title: 'Have a referral code?',
          subtitle: 'Optional - if someone referred you, enter their code here',
          placeholder: 'A13429',
          apply: 'Apply',
          applying: 'Applying...',
          applied: 'Applied',
          appliedMessage: 'Referral code applied successfully.',
          applyError: 'Could not apply referral code.',
        },
        backHome: 'Back to top',
        getStarted: "Let's get started",
        footer: "We'll only contact you about your case",
        companyLine1: 'Global Mobility is a trade name of',
        companyName: 'Global Mobility Group LLC',
        companyAddress: '140 E Broadway Ave, Suite 25, Jackson, WY 83001, USA',
        companyLine2: 'EU Representative: Ana Maria Travieso Hernandez -',
        linkTerms: 'Terms of Service',
        linkPrivacy: 'Privacy Policy',
        linkCookies: 'Cookie Policy',
        linkLegal: 'Legal Notice',
        copyright: '© 2026 Global Mobility Group LLC',
      },
      welcomePage: {
        welcomeBadge: 'Welcome back',
        title: 'Your portal is ready',
        body:
          'Welcome back. Your secure client portal is here, where you can complete everything at your own pace and upload documents from your phone. This speeds up your case and reduces errors. Click below to continue.',
        next1: 'Your information is secure in our system',
        next2: 'A specialist is reviewing your case',
        next3: "We'll contact you by phone, WhatsApp, or email",
        next4: "If more than 48 hours have passed, write to us - we'll respond quickly",
        footnote: "We'll only contact you about your case",
        primaryCta: 'Message us on WhatsApp',
        backLink: 'Back to home',
      },
      authCallback: {
        error: 'Something went wrong. Please try again.',
        goHome: 'Go to home',
      },
      notFound: {
        title: 'Page not found',
        hint: 'Access requires a valid partner link with <code>?ref=</code> in the URL.',
      },
      loginError: {
        retry: 'Try again',
        fallback: 'Something went wrong. Please try again.',
        access_denied: 'You denied access or the request was cancelled.',
        missing_code: 'Authorization failed. Please try again.',
        token_exchange_failed: 'Could not complete sign-in. Please try again.',
        no_access_token: 'Sign-in response was invalid. Please try again.',
        userinfo_failed: 'Could not load your profile. Please try again.',
        email_not_provided: 'Your account did not provide an email.',
        user_not_registered: 'No account found with this Google account. Please sign up with phone first.',
      },
      ssoSignup: {
        newAccount: 'New Account',
        step2of2: 'Step 2 of 2',
        step3of3: 'Step 3 of 3',
        takesLessThanMinute: 'Less than 1 minute',
        almostDone: 'Almost done',
        detailsTitle: "<accent>Let's begin</accent>",
        detailsTitlePlain: "Let's get started",
        detailsSubtitle: 'Just some quick details',
        detailsContinueTerms:
          'By continuing, you agree to our <terms>Terms of Service</terms>, <privacy>Privacy Policy</privacy>, and <dpa>Data Processing Agreement</dpa>.',
        placeholderFirstName: 'María',
        placeholderLastName: 'García',
        placeholderEmail: 'you@example.com',
        firstName: 'First name',
        lastName: 'Last name',
        forQuestion: 'Who is this application for?',
        forMe: 'For me',
        forSomeoneElse: 'For someone else',
        familyQuestion: 'Are you applying alone or with your family?',
        justMe: 'Just me',
        forMyFamily: 'For my family',
        howManyPeople: 'How many people?',
        includingYourself: 'Including yourself',
        continue: 'Continue',
        noDocsNeeded: 'No documents needed at this stage.',
        guideEverything: "We'll guide you through everything.",
        noDocsNeededShort: "You don't need documents at this stage.",
        guideEverythingShort: "We'll guide you through everything.",
        contactTitle: 'How should we <accent>contact you?</accent>',
        contactSubtitle: "We'll use this to send updates about your case",
        phonePreferred: 'Your phone (preferred)',
        contactBy: "We'll contact you by WhatsApp, text, or email",
        sendCode: 'Send code',
        resend: 'Resend',
        verified: 'Verified',
        enterCode: 'Enter 6-digit code',
        incorrectCode: 'Incorrect code. Please try again.',
        resendCode: 'Resend code',
        inSeconds: 'in {{seconds}}s',
        verifyEmailOptional: 'Verify your email',
        optional: '(optional)',
        updatesOrDocs: 'We may send important updates or documents here',
        emailAsMain: 'Use email as my main contact instead of mobile',
        consent: 'I agree to receive updates about my case by WhatsApp, text, or email',
        unsubscribeAnytime: 'You can unsubscribe anytime',
        continueNextSteps: 'Continue to next steps',
        saving: 'Saving…',
        errors: {
          nameRequired: 'First and last name are required.',
          forRequired: 'Select who this application is for.',
          familyRequired: 'Select whether you are applying alone or with family.',
          saveFailed: 'Could not save. Please try again.',
        },
        onlyCaseContact: 'We only contact you about your case',
      },

      fields: {
        nombre: 'First name',
        apellidos: 'Last name',
        fecha_nacimiento: 'Date of birth',
        nacionalidad: 'Nationality',
        sexo: 'Gender',
        estado_civil: 'Marital status',
        nombre_padre: "Father's name",
        nombre_madre: "Mother's name",
        domicilio: 'Address',
        localidad: 'City',
        provincia: 'Province',
        codigo_postal: 'Postal code',
        telefono: 'Phone',
        email: 'Email',
        numero_pasaporte: 'Passport number',
        numero_nie: 'NIE number',
        fecha_entrada_espana: 'Date of entry to Spain',
        motivo_solicitud: 'Reason for application',
        nombre_representado: 'Applicant first name',
        primer_apellido_representado: 'Applicant first surname',
        segundo_apellido_representado: 'Applicant second surname',
        nacionalidad_representado: 'Applicant nationality',
        numero_nie_representado: 'Applicant NIE',
        numero_pasaporte_representado: 'Applicant passport number',
        fecha_nacimiento_representado: 'Applicant date of birth',
        lugar_nacimiento_representado: 'Place of birth (city)',
        pais_nacimiento_representado: 'Country of birth',
        nombre_padre_representado: "Applicant father's name",
        nombre_madre_representado: "Applicant mother's name",
        estado_civil_representado: 'Applicant marital status',
        domicilio_representado: 'Applicant address',
        numero_domicilio_representado: 'House/apartment number',
        localidad_representado: 'Applicant city',
        codigo_postal_representado: 'Applicant postal code',
        provincia_representado: 'Applicant province',
        telefono_representado: 'Applicant phone',
        email_representado: 'Applicant email',
        tipo_tramite: 'Procedure type',
        documento_representante: 'Representative document (DNI/NIE/Passport)',
        empresa_representante: 'Representative company',
        nombre_representante: 'Representative first name',
        primer_apellido_representante: 'Representative first surname',
        segundo_apellido_representante: 'Representative second surname',
        domicilio_representante: 'Representative address',
        numero_domicilio_representante: 'Representative house/apartment number',
        localidad_representante: 'Representative city',
        codigo_postal_representante: 'Representative postal code',
        provincia_representante: 'Representative province',
        telefono_representante: 'Representative phone',
        email_representante: 'Representative email',
        notificaciones_electronicas: 'Electronic notifications consent',
        lugar_firma: 'Place of signature',
        fecha_firma: 'Signature date',
        firma_solicitante: 'Applicant signature',
      },

      welcome: {
        back: 'Welcome back!',
        title: 'Welcome,',
        sub: "Your account is ready. Let's get you your <strong>Spain residency</strong> step by step.",
        pill: 'Your specialist is available now',
        cta: 'Start my application →',
        nextHeading: 'WHAT HAPPENS NEXT',
        contactTitle: "We'll contact you within 48 hours",
        contactDesc: 'A dedicated specialist will reach out to introduce themselves and walk you through the first steps.',
        continueToApp: 'Continue to my application',
        successBody: 'Your registration was successful. Welcome to <strong>Global Mobility</strong> - we\'re excited to help you with your application.',
      },

      dashboard: {
        welcomeBack: 'Welcome back 👋',
        yourActiveProgram: 'Your active program',
        programTitle: 'Extraordinary Regularization 2026',
        programSub: 'Submission window: April - June 30, 2026',
        progress: 'Progress',
        ofCompleted: '{{count}} of {{total}} completed',
        continueApplication: '▶ Continue application',
        deadline: 'Deadline',
        yourTodoList: 'Your to-do list',
        seeAll: 'See all →',
        personalDetails: 'Personal details',
        personalDetailsSub: 'Name, phone and email',
        residenceInfo: 'Residence information',
        residenceInfoSub: 'Current address in Spain',
        passportId: 'Passport or ID',
        passportIdSub: 'Valid government-issued document',
        municipalReg: 'Municipal registration',
        municipalRegSub: 'Proof of address certificate',
        criminalCheck: 'Criminal record check',
        criminalCheckSub: 'Certificate of criminal background',
        done: 'Done',
        active: 'Active',
        pending: 'Pending',
        untilDeadline: 'Until deadline',
        days: 'days',
        docsUploaded: 'Docs uploaded',
        yourSpecialist: 'Your specialist',
        specialistRole: 'Immigration Specialist · Spain',
        availableNow: 'Available now · Responds in ~5 min',
        busy: 'Busy · Will respond soon',
        offline: 'Offline · Will contact when available',
        chat: '💬 Chat',
        nav: { home: 'Home', application: 'Application', support: 'Support', profile: 'Profile', global: 'Global' },
      },

      chat: {
        title: 'Application chat',
        backToDashboard: 'Back to dashboard',
        talkToHuman: 'Talk to human',
        viewToggle: 'Switch between chat and form',
        viewChat: 'Chat',
        viewForm: 'Form',
        queue: 'Queue #{{position}}',
        agentConnected: 'Agent connected',
        empty: "Welcome. I can help you fill out your immigration form. How can I assist you today?",
        placeholder: 'Type your message...',
        errorSendingMessage: 'Error sending message. Please try again.',
      },

      dataPanel: {
        completionProgress: 'Completion progress',
        collectedData: 'Data collected',
        missingFields: 'Fields pending',
        hideCollected: 'Hide',
        showCollected: 'Show',
        hideMissing: 'Hide',
        showMissing: 'Show',
        more: 'more',
        downloadData: 'Download data',
        viewForm: 'View form',
        signature: 'Signature',
        signatureCaptured: 'Signature captured',
        editSignature: 'Edit signature',
        addSignature: 'Add signature',
        drawSignature: 'Draw',
        uploadSignature: 'Upload',
        clear: 'Clear',
        cancel: 'Cancel',
        saveSignature: 'Save',
      },

      upload: {
        uploadFile: 'Upload file',
        dropFileHere: 'Drop your file here or click to browse',
        supportedFormats: 'PDF, JPG, PNG, HEIC - stored as PDF (max 10MB)',
        noFileSelected: 'No file selected.',
        fileTypeNotSupported: 'File type not supported.',
        fileTooLarge: 'File is too large (max 10MB).',
        tips: 'Tips',
        tip1: 'Accepted: PDF, Word, TXT, images.',
        tip2: 'Max size: 10MB.',
        tip3: 'Uploaded documents are linked to your application.',
        errorUploading: 'Error uploading file. Please try again.',
      },

      gettingStarted: {
        title: 'Getting started',
        whoWeAre: 'Who we are',
        whoWeAreContent: 'We are an immigration case management platform that combines AI guidance with professional review. Our virtual assistant is backed by real immigration expertise to help you through every step.',
        whatToExpect: 'What to expect',
        whatToExpectContent: 'You will answer questions and upload documents in a guided flow. We help collect the right information. Your case is then reviewed by our team before submission. You can track status and messages in one place.',
        whatIsRequired: 'What is required',
        whatIsRequiredContent: 'A valid passport, proof of address where applicable, and any documents specific to your pathway. We will tell you exactly what you need as you go.',
        support: 'Our support',
        supportContent: 'You can reach our specialists via in-app chat or phone. We support you in English and Spanish.',
        letsGo: "Let's go!",
      },

      legal: {
        terms: 'Terms',
        termsPlaceholder: 'Terms & Conditions will be published here. Please contact us for the current terms.',
        privacy: 'Privacy',
        privacyPlaceholder: 'Privacy Policy will be published here. We process your data in accordance with applicable regulations.',
        cookiePolicy: 'Cookie policy',
        cookiePolicyContent: 'We use essential cookies to run the service (e.g. session, authentication) and optional cookies to improve experience. You can change your preferences at any time. For full details see our Privacy Policy.',
        goHome: 'Go to home',
        termsOfService: 'Terms of Service',
        privacyPolicy: 'Privacy Policy',
        title: 'Legal',
      },

      cookie: {
        title: 'Cookie notice',
        text: 'We use cookies to provide the service and improve your experience. By continuing you agree to our use of cookies.',
        accept: 'Accept',
      },

      settings: {
        title: 'Settings',
        preferences: 'Preferences',
        language: 'Language',
      },

      account: {
        title: 'Account',
        referFriends: 'Refer your friends',
        settings: 'Account settings',
        editProfile: 'Edit profile',
        actions: 'Actions',
        deleteAccount: 'Delete account',
        deleteAccountTitle: 'Delete account?',
        deleteAccountWarning: 'This will permanently remove your account and all your data. This cannot be undone.',
        deleteAccountConfirm: 'Delete my account',
      },

      formViewer: {
        error: 'Error loading form',
        print: 'Print',
        download: 'Download',
        retry: 'Retry',
      },

      formNames: {
        EX17: 'EX-17',
        REPRESENTATIVE: 'Representative',
        UNIFIED: 'All forms',
        EX10: 'EX-10',
      },

      form: {
        title: 'Fill in details',
        applicationFormLabel: 'Regularization 2026',
        syncHint: 'Changes save automatically and update progress.',
        sectionPersonalInfo: 'Personal information',
        sectionFamilyInfo: 'Family information',
        sectionContactInfo: 'Contact information',
        sectionDocumentInfo: 'Document information',
        sectionOther: 'Other fields',
        sectionApplicantDetails: 'Applicant details',
        sectionAuthorization: 'Authorization',
        sectionRepresentativeDetails: 'Representative details',
        sectionNotifications: 'Notifications',
        sectionSignature: 'Signature',
        allowElectronicNotifications: 'Allow electronic notifications',
      },

      errorOccurred: 'Something went wrong. Please try again.',

      refer: {
        title: 'Refer your friends',
        desc: 'Share the app with people you know. You get credit for each referred user who signs up.',
        yourCode: 'Your referral code',
        copy: 'Copy',
        copied: 'Copied!',
        whoUsed: 'Who used my referral',
      },

      referrals: {
        title: 'Who used my referral',
        desc: 'People who signed up using your referral code.',
        empty: 'No one has used your referral code yet.',
      },

      admin: {
        allCases: 'All Cases',
        agentConsole: 'Agent Console',
        registeredUsers: 'Registered users',
        createUser: 'Create user',
        createUserModalTitle: 'Create user',
        createUserFirstName: 'First name',
        createUserLastName: 'Last name',
        createUserEmail: 'Email (optional)',
        createUserPhone: 'Phone (optional)',
        createUserCancel: 'Cancel',
        createUserSubmit: 'Create and open case',
        createUserError: 'Could not create user. Please try again.',
        openAgentConsole: 'Open Agent Console',
        manageQueue: 'Manage live chat queue and talk to users who requested a human agent.',
        totalCases: 'Sessions (cases) from MongoDB. Total: {{count}}',
        searchPlaceholder: 'Search by email, name, or phone...',
        statusAll: 'Status: All',
        statusActive: 'Active',
        statusCompleted: 'Completed',
        statusArchived: 'Archived',
        loadingCases: 'Loading cases...',
        noCasesMatch: 'No cases match.',
        viewDetails: 'View details',
        caseDetails: 'Case details',
        requestReupload: 'Request document re-upload',
        agent: {
          activeSessions: 'Active sessions',
          openChat: 'Open chat',
          closeChat: 'Close chat',
          talkToUser: 'Talk to user',
          placeholder: 'Type your message...',
          send: 'Send',
        },
        caseDetail: {
          user: 'User',
          session: 'Session',
          status: 'Status',
          completion: 'Completion',
          allFields: 'All fields',
          editHint: 'Click Edit to change a value, or Fill to add a missing one. Changes save to the case.',
          field: 'Field',
          value: 'Value',
          action: 'Action',
          save: 'Save',
          edit: 'Edit',
          fill: 'Fill',
          empty: '-',
          loadError: 'Failed to load case details.',
          updateError: 'Failed to update field.',
          selectDocType: 'Please select a document type',
          reuploadSent: 'Reupload request sent to user.',
          reuploadFailed: 'Failed to send reupload request.',
          notFound: 'Case not found.',
          requestReupload: 'Request document re-upload',
          documentTypePlaceholder: 'Document type',
          reasonPlaceholder: 'Reason (optional)',
          send: 'Send',
        },
      },

      language: { en: 'EN', es: 'ES' },
    },
  },
  es: {
    translation: {
      brandName: 'GLOBAL MOBILITY',
      loading: 'Cargando...',
      signingIn: 'Iniciando sesión...',
      logOut: 'Cerrar sesión',
      back: 'Atrás',
      landing: {
        eyebrow: 'ESPAÑA · REGULARIZACIÓN EXTRAORDINARIA 2026',
        title1: '¿Puedes obtener',
        title2: 'residencia',
        title3: 'legal',
        title4: 'en',
        titleYear: '2026?',
        intro1: 'Si ya estás en España, esta puede ser tu oportunidad para obtener residencia legal. Te ayudamos a hacerlo correctamente.',
        intro2: 'La ventana cierra el <strong>30 de junio de 2026</strong>. Si esperas - o cometes un error - puedes perder esta oportunidad.',
        intro3: 'Trabaja con nosotros - bien hecho desde el primer momento.',
        ctaMain: 'Comprobar si califico - es gratis',
        ctaSubline: '{{price}} todo incluido · solo pagas al presentar',
        ctaNote: 'Sin pago por adelantado · Solo se cobra cuando tu solicitud se presenta',
        attorneySection: 'TU ABOGADA',
        attorneyRole: 'Abogada de Extranjería · España',
        attorneyStats: '10+ años de experiencia · 1,500+ casos gestionados',
        attorneyHandle: '@travieso.global',
        attorneyFirmBadge: 'Travesio Abogada de Extranjería',
        available: 'Disponible',
        hideCreds: 'Ocultar credenciales',
        viewCreds: 'Ver credenciales completas',
        bioP1: 'Ayudo a personas que ya viven en España a obtener la residencia legal de forma clara, segura y sin errores.',
        bioP2: 'Mi enfoque combina experiencia legal con un proceso de revisión estructurado para garantizar que las solicitudes se presentan completas y correctamente desde el primer intento.',
        bioP3: 'Con más de <strong>10 años de experiencia</strong> en derecho de extranjería, he gestionado más de <strong>1.500 casos</strong>, guiando a personas a lo largo del proceso y ayudándoles a evitar denegaciones e incidencias administrativas. También he asesorado a personas a través del servicio jurídico de inmigración y protección internacional del Ilustre Colegio de Abogados de Madrid.',
        bioP4: 'Entiendo lo importante que es esta oportunidad - y lo fácil que es perderla por un pequeño error. Por eso cada caso se revisa con detalle para garantizar que la solicitud está completa, correcta y lista para presentar.',
        quote: 'He gestionado más de 1.500 casos de extranjería en España. Lo que sé es esto: la mayoría de las denegaciones no son por falta de elegibilidad - son por documentación incorrecta o incompleta. Eso es exactamente lo que evitamos. Si calificas, quiero asegurarme de que un error de papeleo no te cueste tu oportunidad.',
        whyTitle: 'Por qué',
        whyAccent: 'Global Mobility',
        why1Title: 'Una persona real para tu caso',
        why1Desc: 'Tendrás un especialista dedicado desde el primer día que conoce tu expediente y está disponible cuando lo necesitas.',
        why2Title: 'Sin coste inicial - pagas solo al éxito',
        why2Desc: 'Tarifa fija de €250, solo se cobra cuando tu solicitud se presenta.',
        why2Strong: '',
        why3Title: 'Revisión de abogada en cada solicitud',
        why3Desc: 'Antes de realizar la presentación, un abogado de inmigración colegiado  revisará cada detalle de su expediente.',
        why4Title: 'Nos encargamos de todo',
        why4Desc1: 'Desde el primer documento hasta la presentación final',
        why4Desc2: 'No tienes que lidiar con el portal del gobierno ni con burocracias absurdas.',
        why4Desc3: 'Lo hacemos nosotros.',
        why5Title: 'Diseñado para evitar errores',
        why5Desc: 'Combinamos experiencia legal real con un proceso de revisión estructurado para identificar y corregir problemas antes de la presentación.',
        howTitle: 'Cómo',
        howAccent: 'funciona',
        step1Title: 'Empieza (2 minutos)',
        step1Desc: 'Un especialista te contactará por WhatsApp en 24 horas - sin papeleo todavía.',
        step2Title: 'Guía paso a paso',
        step2Desc: 'Un especialista dedicado trabaja contigo para recopilar y organizar tus documentos.',
        step3Title: 'Revisión legal',
        step3Desc: 'Una abogada de extranjería colegiada revisa tu solicitud completa <strong>antes de presentarla.</strong>',
        step4Title: 'Presentación y seguimiento',
        step4Desc: 'Presentamos tu caso y te mantenemos informado hasta la resolución.',
        pricingAria: 'Precios y registro',
        pricingTitle: 'Precios',
        allInclusive: 'TODO INCLUIDO',
        checkingOffer: 'Comprobando oferta...',
        perPerson: 'Por persona',
        noUpfront: 'Sin pago por adelantado - ni un euro antes de la presentación',
        successFee: 'Solo se cobra cuando tu solicitud está completa y presentada. Sin sorpresas.',
        whatIncludes: 'Lo que incluye',
        priceList1: 'Especialista dedicado desde el primer día',
        priceList2: 'Revisión completa por abogado antes de presentar',
        priceList3: 'Actualizaciones hasta la decisión final',
        partner: 'Partner: {{name}}',
        ctaBoxTitle: 'No pierdas tu oportunidad de regularizarte en España.',
        ctaBoxDesc: 'El tiempo es limitada - y los errores pueden costarte esta oportunidad.',
        ctaBoxButton: 'Comprobar si califico - es gratis',
        ctaFreeNote: 'Es gratis - sin compromiso',
        ctaLegal: 'Tu información está protegida y se usa únicamente para gestionar tu solicitud. Al registrarte, aceptas nuestros términos y política de privacidad.',
        footer: {
          companyLine1: 'Global Mobility es un nombre comercial de',
          companyName: 'Global Mobility Group LLC',
          companyAddress: '140 E Broadway Ave, Suite 25, Jackson, WY 83001, USA',
          companyLine2: 'Representante en la UE: Ana María Travieso Hernández -',
          linkTerms: 'Términos del Servicio',
          linkPrivacy: 'Política de Privacidad',
          linkCookies: 'Política de Cookies',
          linkLegal: 'Aviso Legal',
          copyright: '© 2026 Global Mobility Group LLC',
        },
      },
      signup: {
        signIn: 'INICIAR SESIÓN',
        newAccount: 'Nueva cuenta',
        mobileTitle: 'Primero, introduce<br />tu <em>número de móvil</em>',
        mobileTitleSignIn: 'Introduce tu <em>móvil</em> para iniciar sesión',
        mobileDesc:
          'Tu información se usa únicamente para gestionar tu solicitud y está protegida por nuestra <privacy>Política de Privacidad</privacy>.',
        continue: 'Continuar',
        or: 'o',
        secondTitle: 'Segundo, ¿tienes <em>WhatsApp?</em>',
        secondDesc: 'Elige cómo quieres recibir actualizaciones, documentos y mensajes de tu especialista.',
        whatsappDesc: 'Recibe documentos y actualizaciones directamente por WhatsApp.',
        smsTitle: 'Móvil (SMS)',
        smsDesc: 'Mensajes de texto estándar. Funciona sin internet.',
        tip: 'Puedes cambiar tu canal preferido en cualquier momento desde la configuración de tu cuenta.',
        sending: 'Enviando...',
        otpTitle: 'Ingresa el código que te enviamos',
        otpDesc: 'Enviamos un código de un solo uso a tu {{channel}}.',
        otpPlaceholder: 'Ingresa código de 6 dígitos',
        verifying: 'Verificando...',
        otpResendTimerPrefix: 'Reenviar en ',
        otpResendReady: 'Ya puedes reenviar el código.',
        otpResendCode: 'Reenviar código',
        terms: 'Al continuar, aceptas nuestros <terms>Términos de Servicio</terms>, <privacy>Política de Privacidad</privacy> y <dpa>Acuerdo de Tratamiento de Datos</dpa>.',
        noAccount: '¿No tienes una cuenta?',
        signUpCta: 'Regístrate →',
        haveAccount: '¿Ya tienes una cuenta?',
        signInCta: 'Inicia sesión →',
        errors: {
          invalidPhone: 'Por favor, ingresa un número de teléfono válido.',
          numberAlreadyRegistered: 'Este número ya está registrado. Por favor, inicia sesión.',
          enterCode: 'Por favor, ingresa el código que recibiste.',
          invalidResponse: 'Respuesta inválida. Inténtalo de nuevo.',
          invalidOtp: 'Código inválido o expirado.',
          sendFailed: 'No se pudo enviar el código. Inténtalo de nuevo.',
          requestTimeout: 'El servidor está tardando demasiado en responder. Inténtalo de nuevo.',
          checkPhoneFailed: 'No se pudo verificar tu número. Inténtalo de nuevo.',
          ssoGeneric: 'No se pudo completar el inicio de sesión. Inténtalo de nuevo.',
          emailExists: 'Este correo ya está registrado. Inicia sesión en su lugar.',
          accessDenied: 'Se canceló el inicio de sesión o se denegó el acceso. Inténtalo de nuevo.',
          sessionFailed: 'No se pudo establecer la sesión. Inténtalo de nuevo.',
          tokenExchangeFailed: 'No se pudo completar el inicio de sesión. Inténtalo de nuevo.',
          userNotRegistered: 'Esta cuenta no está registrada. Regístrate primero.',
          phoneLoginNotRegistered: 'Usuario no registrado. Crea una cuenta primero.',
          invalidRequest: 'Solicitud de inicio de sesión inválida. Inténtalo de nuevo.',
          serverError: 'Algo salió mal de nuestro lado. Inténtalo más tarde.',
        },
      },
      qualify: {
        eyebrow: 'Verificación de elegibilidad',
        badge: 'Menos de 1 minuto',
        title: 'Comprueba si <accent>calificas</accent>',
        subtitle: 'Responde unas preguntas rápidas de sí/no para saber si calificas para la vía de regularización de España 2026.',
        yes: 'Sí',
        no: 'No',
        altTitle: 'Exploremos <accent>otras opciones</accent>',
        altDesc: 'Según tus respuestas, es posible que no califiques para la Regularización Extraordinaria 2026. Sin embargo, puede haber otras opciones legales disponibles para ti. Continúa para registrarte y un especialista revisará tu caso para identificar el mejor camino a seguir.',
        continue: 'Continuar',
        startOver: 'Empezar de nuevo',
        privacy: 'Tus respuestas son confidenciales y están protegidas por nuestra Política de Privacidad.',
        q1: '¿Llegaste a España antes del 1 de enero de 2026?',
        q2: '¿Alguna vez solicitaste asilo en España?',
        q3: '¿Solicitaste asilo en 2026?',
        q4: '¿Tu solicitud de asilo fue concedida?',
        q5: '¿Has sido condenado por un delito en España?',
        qualifiedBadge: 'Elegibilidad confirmada',
        qualifiedTitle: 'Buenas noticias - calificas para la <accent>2026 Regularización Extraordinaria.</accent>',
        qualifiedSub: 'Avancemos. Nuestro equipo te guiará en cada paso de tu solicitud - desde la revisión de documentos hasta la presentación final.',
        qualifiedPrivacy:
          'Tus respuestas son confidenciales y están protegidas por nuestra <privacy>Política de Privacidad</privacy>.',
      },
      successPage: {
        brandLogo: 'G',
        brandTitle: 'Global Mobility',
        brandSubtitle: 'Spain Regularization 2026',
        completedBadge: 'Registro completado',
        title: '¡Todo listo!',
        body: 'Te contactaremos en 24-48 horas para los próximos pasos.',
        next1: 'Revisamos tu información',
        next2: 'Te contactamos por teléfono o correo',
        next3: 'Te guiamos en los siguientes pasos',
        next4: 'Puedes ir preparando: pasaporte, prueba de estancia y documentos de apoyo',
        referral: {
          ariaLabel: '¿Tienes un código de referido?',
          title: '¿Tienes un código de referido?',
          subtitle: 'Opcional - si alguien te refirió, introduce aquí su código',
          placeholder: 'A13429',
          apply: 'Aplicar',
          applying: 'Aplicando...',
          applied: 'Aplicado',
          appliedMessage: 'Código de referido aplicado correctamente.',
          applyError: 'No se pudo aplicar el código de referido.',
        },
        backHome: 'Volver al inicio',
        footer: 'Solo te contactaremos sobre tu caso',
        companyLine1: 'Global Mobility es un nombre comercial de',
        companyName: 'Global Mobility Group LLC',
        companyAddress: '140 E Broadway Ave, Suite 25, Jackson, WY 83001, USA',
        companyLine2: 'Representante en la UE: Ana Maria Travieso Hernandez -',
        linkTerms: 'Términos del Servicio',
        linkPrivacy: 'Política de Privacidad',
        linkCookies: 'Política de Cookies',
        linkLegal: 'Aviso Legal',
        copyright: '© 2026 Global Mobility Group LLC',
      },
      welcomePage: {
        welcomeBadge: 'Bienvenido de nuevo',
        title: 'Tu portal está listo',
        body:
          'Bienvenido de nuevo. Aquí tienes tu portal seguro de cliente, donde puedes completar todo a tu ritmo y subir documentos desde tu teléfono. Esto acelera tu caso y reduce errores. Haz clic abajo para continuar.',
        next1: 'Tu información está segura en nuestro sistema',
        next2: 'Un especialista está revisando tu caso',
        next3: 'Te contactaremos por teléfono, WhatsApp o correo',
        next4: 'Si han pasado más de 48 horas, escríbenos - te respondemos rápido',
        footnote: 'Solo te contactaremos sobre tu caso',
        primaryCta: 'Escríbenos por WhatsApp',
        backLink: 'Volver al inicio',
      },
      authCallback: {
        error: 'Algo salió mal. Inténtalo de nuevo.',
        goHome: 'Ir al inicio',
      },
      notFound: {
        title: 'Página no encontrada',
        hint: 'El acceso requiere un enlace válido de partner con <code>?ref=</code> en la URL.',
      },
      loginError: {
        retry: 'Intentar de nuevo',
        fallback: 'Algo salió mal. Inténtalo de nuevo.',
        access_denied: 'Has denegado el acceso o se canceló la solicitud.',
        missing_code: 'Falló la autorización. Inténtalo de nuevo.',
        token_exchange_failed: 'No se pudo completar el inicio de sesión. Inténtalo de nuevo.',
        no_access_token: 'La respuesta de inicio de sesión fue inválida. Inténtalo de nuevo.',
        userinfo_failed: 'No se pudo cargar tu perfil. Inténtalo de nuevo.',
        email_not_provided: 'Tu cuenta no proporcionó un correo electrónico.',
        user_not_registered: 'No se encontró una cuenta con esta cuenta de Google. Regístrate primero con teléfono.',
      },
      ssoSignup: {
        newAccount: 'Nueva cuenta',
        step2of2: 'Paso 2 de 2',
        step3of3: 'Paso 3 de 3',
        takesLessThanMinute: 'Menos de 1 minuto',
        almostDone: 'Casi listo',
        detailsTitle: '<accent>Empecemos</accent>',
        detailsTitlePlain: 'Empecemos',
        detailsSubtitle: 'Solo unos datos rápidos',
        detailsContinueTerms:
          'Al continuar, aceptas nuestros <terms>Términos de Servicio</terms>, <privacy>Política de Privacidad</privacy> y <dpa>Acuerdo de Tratamiento de Datos</dpa>.',
        placeholderFirstName: 'María',
        placeholderLastName: 'García',
        placeholderEmail: 'tu@ejemplo.com',
        firstName: 'Nombre',
        lastName: 'Apellido',
        forQuestion: '¿Para quién es esta solicitud?',
        forMe: 'Para mí',
        forSomeoneElse: 'Para otra persona',
        familyQuestion: '¿Solicitas solo o con tu familia?',
        justMe: 'Solo yo',
        forMyFamily: 'Para mi familia',
        howManyPeople: '¿Cuántas personas?',
        includingYourself: 'Incluyéndote',
        continue: 'Continuar',
        noDocsNeeded: 'No se necesitan documentos en esta etapa.',
        guideEverything: 'Te guiaremos en todo el proceso.',
        noDocsNeededShort: 'No necesitas documentos en esta etapa.',
        guideEverythingShort: 'Te guiaremos en todo.',
        contactTitle: '¿Cómo debemos <accent>contactarte</accent>?',
        contactSubtitle: 'Usaremos esto para enviarte actualizaciones de tu caso',
        phonePreferred: 'Tu teléfono (preferido)',
        contactBy: 'Te contactaremos por WhatsApp, SMS o correo electrónico',
        sendCode: 'Enviar código',
        resend: 'Reenviar',
        verified: 'Verificado',
        enterCode: 'Ingresa el código de 6 dígitos',
        incorrectCode: 'Código incorrecto. Inténtalo de nuevo.',
        resendCode: 'Reenviar código',
        inSeconds: 'en {{seconds}}s',
        verifyEmailOptional: 'Tu correo electrónico',
        optional: '(opcional)',
        updatesOrDocs: 'Puede que te enviemos actualizaciones importantes o documentos aquí',
        emailAsMain: 'Usar el correo electrónico como contacto principal en lugar del móvil',
        consent: 'Acepto recibir actualizaciones de mi caso por WhatsApp, SMS o correo electrónico',
        unsubscribeAnytime: 'Puedes darte de baja en cualquier momento',
        continueNextSteps: 'Continuar a los siguientes pasos',
        saving: 'Guardando…',
        errors: {
          nameRequired: 'El nombre y los apellidos son obligatorios.',
          forRequired: 'Selecciona para quién es esta solicitud.',
          familyRequired: 'Selecciona si solicitas solo o con familia.',
          saveFailed: 'No se pudo guardar. Inténtalo de nuevo.',
        },
        onlyCaseContact: 'Solo te contactamos sobre tu caso',
      },

      fields: {
        nombre: 'Nombre',
        apellidos: 'Apellidos',
        fecha_nacimiento: 'Fecha de nacimiento',
        nacionalidad: 'Nacionalidad',
        sexo: 'Sexo',
        estado_civil: 'Estado civil',
        nombre_padre: 'Nombre del padre',
        nombre_madre: 'Nombre de la madre',
        domicilio: 'Domicilio',
        localidad: 'Localidad',
        provincia: 'Provincia',
        codigo_postal: 'Código postal',
        telefono: 'Teléfono',
        email: 'Correo electrónico',
        numero_pasaporte: 'Número de pasaporte',
        numero_nie: 'Número de NIE',
        fecha_entrada_espana: 'Fecha de entrada a España',
        motivo_solicitud: 'Motivo de la solicitud',
        nombre_representado: 'Nombre del solicitante',
        primer_apellido_representado: 'Primer apellido del solicitante',
        segundo_apellido_representado: 'Segundo apellido del solicitante',
        nacionalidad_representado: 'Nacionalidad del solicitante',
        numero_nie_representado: 'NIE del solicitante',
        numero_pasaporte_representado: 'Pasaporte del solicitante',
        fecha_nacimiento_representado: 'Fecha de nacimiento del solicitante',
        lugar_nacimiento_representado: 'Lugar de nacimiento (ciudad)',
        pais_nacimiento_representado: 'País de nacimiento',
        nombre_padre_representado: 'Nombre del padre',
        nombre_madre_representado: 'Nombre de la madre',
        estado_civil_representado: 'Estado civil del solicitante',
        domicilio_representado: 'Domicilio en España',
        numero_domicilio_representado: 'Número de casa/departamento',
        localidad_representado: 'Localidad',
        codigo_postal_representado: 'Código postal',
        provincia_representado: 'Provincia',
        telefono_representado: 'Teléfono',
        email_representado: 'Email',
        tipo_tramite: 'Tipo de trámite',
        documento_representante: 'DNI/NIE/Pasaporte del representante',
        empresa_representante: 'Empresa del representante',
        nombre_representante: 'Nombre del representante',
        primer_apellido_representante: 'Primer apellido del representante',
        segundo_apellido_representante: 'Segundo apellido del representante',
        domicilio_representante: 'Domicilio del representante',
        numero_domicilio_representante: 'Número de casa/departamento (representante)',
        localidad_representante: 'Localidad (representante)',
        codigo_postal_representante: 'Código postal (representante)',
        provincia_representante: 'Provincia (representante)',
        telefono_representante: 'Teléfono del representante',
        email_representante: 'Email del representante',
        notificaciones_electronicas: 'Consentimiento de notificaciones electrónicas',
        lugar_firma: 'Lugar de firma',
        fecha_firma: 'Fecha de firma',
        firma_solicitante: 'Firma del solicitante',
      },

      welcome: {
        back: '¡Bienvenido de nuevo!',
        title: 'Bienvenido,',
        sub: 'Tu cuenta está lista. Vamos a conseguirte la <strong>residencia en España</strong> paso a paso.',
        pill: 'Tu especialista está disponible ahora',
        cta: 'Comenzar mi solicitud →',
        nextHeading: 'QUÉ SIGUE',
        contactTitle: 'Nos pondremos en contacto en 48 horas',
        contactDesc: 'Un especialista se pondrá en contacto para presentarse y guiarte en los primeros pasos.',
        continueToApp: 'Continuar a mi solicitud',
        successBody: 'Tu registro fue exitoso. Bienvenido a <strong>Global Mobility</strong> - estamos emocionados de ayudarte con tu solicitud.',
      },

      dashboard: {
        welcomeBack: 'Bienvenido de nuevo 👋',
        yourActiveProgram: 'Tu programa activo',
        programTitle: 'Regularización Extraordinaria 2026',
        programSub: 'Ventana de presentación: abril - 30 junio 2026',
        progress: 'Progreso',
        ofCompleted: '{{count}} de {{total}} completados',
        continueApplication: '▶ Continuar solicitud',
        deadline: 'Fecha límite',
        yourTodoList: 'Tu lista de tareas',
        seeAll: 'Ver todo →',
        personalDetails: 'Datos personales',
        personalDetailsSub: 'Nombre, teléfono y correo',
        residenceInfo: 'Datos de residencia',
        residenceInfoSub: 'Dirección actual en España',
        passportId: 'Pasaporte o DNI',
        passportIdSub: 'Documento oficial válido',
        municipalReg: 'Empadronamiento',
        municipalRegSub: 'Certificado de empadronamiento',
        criminalCheck: 'Antecedentes penales',
        criminalCheckSub: 'Certificado de antecedentes',
        done: 'Hecho',
        active: 'Activo',
        pending: 'Pendiente',
        untilDeadline: 'Hasta la fecha límite',
        days: 'días',
        docsUploaded: 'Documentos subidos',
        yourSpecialist: 'Tu especialista',
        specialistRole: 'Especialista en Inmigración · España',
        availableNow: 'Disponible ahora · Responde en ~5 min',
        busy: 'Ocupado · Responderá pronto',
        offline: 'Desconectado · Se pondrá en contacto cuando esté disponible',
        chat: '💬 Chat',
        nav: { home: 'Inicio', application: 'Solicitud', support: 'Soporte', profile: 'Perfil', global: 'Global' },
      },

      chat: {
        title: 'Chat de solicitud',
        backToDashboard: 'Volver al inicio',
        talkToHuman: 'Hablar con una persona',
        viewToggle: 'Cambiar entre chat y formulario',
        viewChat: 'Chat',
        viewForm: 'Formulario',
        queue: 'Cola #{{position}}',
        agentConnected: 'Agente conectado',
        empty: 'Hola. Puedo ayudarte a completar tu formulario de inmigración. ¿En qué puedo ayudarte hoy?',
        placeholder: 'Escribe tu mensaje...',
        errorSendingMessage: 'Error al enviar el mensaje. Inténtalo de nuevo.',
      },

      dataPanel: {
        completionProgress: 'Progreso de completado',
        collectedData: 'Datos recogidos',
        missingFields: 'Campos pendientes',
        hideCollected: 'Ocultar',
        showCollected: 'Mostrar',
        hideMissing: 'Ocultar',
        showMissing: 'Mostrar',
        more: 'más',
        downloadData: 'Descargar datos',
        viewForm: 'Ver formulario',
        signature: 'Firma',
        signatureCaptured: 'Firma capturada',
        editSignature: 'Editar firma',
        addSignature: 'Añadir firma',
        drawSignature: 'Dibujar',
        uploadSignature: 'Subir',
        clear: 'Borrar',
        cancel: 'Cancelar',
        saveSignature: 'Guardar',
      },

      upload: {
        uploadFile: 'Subir archivo',
        dropFileHere: 'Arrastra el archivo o haz clic para seleccionar',
        supportedFormats: 'PDF, JPG, PNG, HEIC - se guardan como PDF (máx. 10MB)',
        noFileSelected: 'No se ha seleccionado ningún archivo.',
        fileTypeNotSupported: 'Tipo de archivo no admitido.',
        fileTooLarge: 'El archivo es demasiado grande (máx. 10MB).',
        tips: 'Consejos',
        tip1: 'Aceptados: PDF, JPG, PNG, HEIC (se guardan como PDF en el servidor).',
        tip2: 'Tamaño máximo: 10MB.',
        tip3: 'Los documentos se vinculan a tu solicitud.',
        errorUploading: 'Error al subir el archivo. Inténtalo de nuevo.',
      },

      gettingStarted: {
        title: 'Comenzar',
        whoWeAre: 'Quiénes somos',
        whoWeAreContent: 'Somos una plataforma de gestión de casos de inmigración que combina orientación con IA y revisión profesional. Te ayudamos en cada paso.',
        whatToExpect: 'Qué esperar',
        whatToExpectContent: 'Responderás preguntas y subirás documentos en un flujo guiado. Recopilamos la información correcta. Tu caso es revisado por nuestro equipo antes del envío.',
        whatIsRequired: 'Qué se requiere',
        whatIsRequiredContent: 'Pasaporte válido, prueba de domicilio cuando aplique y documentos específicos de tu vía. Te indicaremos exactamente qué necesitas.',
        support: 'Nuestro soporte',
        supportContent: 'Puedes contactar a nuestros especialistas por chat en la app o por teléfono. Te atendemos en inglés y español.',
        letsGo: '¡Vamos!',
      },

      legal: {
        terms: 'Términos',
        termsPlaceholder: 'Los Términos y Condiciones se publicarán aquí. Contacta con nosotros para los términos actuales.',
        privacy: 'Privacidad',
        privacyPlaceholder: 'La Política de Privacidad se publicará aquí. Tratamos tus datos según la normativa aplicable.',
        cookiePolicy: 'Política de cookies',
        cookiePolicyContent: 'Usamos cookies esenciales para el servicio (sesión, autenticación) y opcionales para mejorar la experiencia. Puedes cambiar tus preferencias en cualquier momento.',
        goHome: 'Ir al inicio',
        termsOfService: 'Términos de servicio',
        privacyPolicy: 'Política de privacidad',
        title: 'Legal',
      },

      cookie: {
        title: 'Aviso de cookies',
        text: 'Usamos cookies para el servicio y mejorar tu experiencia. Al continuar aceptas su uso.',
        accept: 'Aceptar',
      },

      settings: {
        title: 'Ajustes',
        preferences: 'Preferencias',
        language: 'Idioma',
      },

      account: {
        title: 'Cuenta',
        referFriends: 'Referir amigos',
        settings: 'Ajustes de cuenta',
        editProfile: 'Editar perfil',
        actions: 'Acciones',
        deleteAccount: 'Eliminar cuenta',
        deleteAccountTitle: '¿Eliminar cuenta?',
        deleteAccountWarning: 'Se eliminará permanentemente tu cuenta y todos tus datos. No se puede deshacer.',
        deleteAccountConfirm: 'Eliminar mi cuenta',
      },

      formViewer: {
        error: 'Error al cargar el formulario',
        print: 'Imprimir',
        download: 'Descargar',
        retry: 'Reintentar',
      },

      formNames: {
        EX17: 'EX-17',
        REPRESENTATIVE: 'Representante',
        UNIFIED: 'Todos los formularios',
        EX10: 'EX-10',
      },

      form: {
        title: 'Completar datos',
        applicationFormLabel: 'Regularization 2026',
        syncHint: 'Los cambios se guardan solos y actualizan el progreso.',
        sectionPersonalInfo: 'Datos personales',
        sectionFamilyInfo: 'Datos familiares',
        sectionContactInfo: 'Datos de contacto',
        sectionDocumentInfo: 'Documentación',
        sectionOther: 'Otros campos',
        sectionApplicantDetails: 'Datos del solicitante',
        sectionAuthorization: 'Autorización',
        sectionRepresentativeDetails: 'Datos del representante',
        sectionNotifications: 'Notificaciones',
        sectionSignature: 'Firma',
        allowElectronicNotifications: 'Permitir notificaciones electrónicas',
      },

      errorOccurred: 'Algo salió mal. Inténtalo de nuevo.',

      refer: {
        title: 'Referir amigos',
        desc: 'Comparte la app con quien conozcas. Obtienes crédito por cada usuario referido que se registre.',
        yourCode: 'Tu código de referido',
        copy: 'Copiar',
        copied: '¡Copiado!',
        whoUsed: 'Quién usó mi referido',
      },

      referrals: {
        title: 'Quién usó mi referido',
        desc: 'Personas que se registraron con tu código de referido.',
        empty: 'Nadie ha usado tu código de referido aún.',
      },

      admin: {
        allCases: 'Todos los casos',
        agentConsole: 'Consola de agente',
        registeredUsers: 'Usuarios registrados',
        createUser: 'Crear usuario',
        createUserModalTitle: 'Crear usuario',
        createUserFirstName: 'Nombre',
        createUserLastName: 'Apellido',
        createUserEmail: 'Correo (opcional)',
        createUserPhone: 'Teléfono (opcional)',
        createUserCancel: 'Cancelar',
        createUserSubmit: 'Crear y abrir caso',
        createUserError: 'No se pudo crear el usuario. Inténtalo de nuevo.',
        openAgentConsole: 'Abrir consola de agente',
        manageQueue: 'Gestiona la cola de chat y habla con usuarios que pidieron un agente.',
        totalCases: 'Sesiones (casos) en MongoDB. Total: {{count}}',
        searchPlaceholder: 'Buscar por correo, nombre o teléfono...',
        statusAll: 'Estado: Todos',
        statusActive: 'Activo',
        statusCompleted: 'Completado',
        statusArchived: 'Archivado',
        loadingCases: 'Cargando casos...',
        noCasesMatch: 'Ningún caso coincide.',
        viewDetails: 'Ver detalles',
        caseDetails: 'Detalles del caso',
        requestReupload: 'Solicitar nueva subida de documento',
        agent: {
          activeSessions: 'Sesiones activas',
          openChat: 'Abrir chat',
          closeChat: 'Cerrar chat',
          talkToUser: 'Hablar con el usuario',
          placeholder: 'Escribe tu mensaje...',
          send: 'Enviar',
        },
        caseDetail: {
          user: 'Usuario',
          session: 'Sesión',
          status: 'Estado',
          completion: 'Completado',
          allFields: 'Todos los campos',
          editHint: 'Haz clic en Editar para cambiar un valor o en Rellenar para añadir uno faltante. Los cambios se guardan en el caso.',
          field: 'Campo',
          value: 'Valor',
          action: 'Acción',
          save: 'Guardar',
          edit: 'Editar',
          fill: 'Rellenar',
          empty: '-',
          loadError: 'Error al cargar los detalles del caso.',
          updateError: 'Error al actualizar el campo.',
          selectDocType: 'Selecciona un tipo de documento',
          reuploadSent: 'Solicitud de nueva subida enviada al usuario.',
          reuploadFailed: 'Error al enviar la solicitud.',
          notFound: 'Caso no encontrado.',
          requestReupload: 'Solicitar nueva subida',
          documentTypePlaceholder: 'Tipo de documento',
          reasonPlaceholder: 'Motivo (opcional)',
          send: 'Enviar',
        },
      },

      language: { en: 'EN', es: 'ES' },
    },
  },
};

// Add additional languages. English is the source of truth.
// For keys not explicitly translated yet, we fall back to English strings (never missing keys).
resources.en.translation.language = { en: 'EN', es: 'ES', fr: 'FR', ar: 'AR', zh: 'ZH' };
resources.es.translation.language = { en: 'EN', es: 'ES', fr: 'FR', ar: 'AR', zh: 'ZH' };

resources.en.translation.advisorPortal = advisorPortalEn;
resources.es.translation.advisorPortal = advisorPortalEs;
resources.en.translation.lpWorkbench = lpWorkbenchEn;
resources.es.translation.lpWorkbench = lpWorkbenchEs;
resources.en.translation.participant = participantEn;
resources.es.translation.participant = participantEs;

resources.fr = {
  translation: {
    ...resources.en.translation,
    cookie: {
      ...(resources.en.translation.cookie || {}),
      title: 'Avis cookies',
      text: "Nous utilisons des cookies pour fournir le service et améliorer votre expérience. En continuant, vous acceptez notre utilisation des cookies.",
      accept: 'Accepter',
    },
    legal: {
      ...(resources.en.translation.legal || {}),
      privacy: 'Confidentialité',
      cookiePolicy: 'Politique de cookies',
    },
    loading: 'Chargement…',
    signingIn: 'Connexion…',
    logOut: 'Se déconnecter',
    back: 'Retour',
    settings: { ...(resources.en.translation.settings || {}), title: 'Paramètres', preferences: 'Préférences', language: 'Langue' },
    account: { ...(resources.en.translation.account || {}), title: 'Compte', settings: 'Paramètres du compte', editProfile: 'Modifier le profil', actions: 'Actions', deleteAccount: 'Supprimer le compte', deleteAccountTitle: 'Supprimer le compte ?', deleteAccountWarning: 'Cela supprimera définitivement votre compte et toutes vos données. Cette action est irréversible.', deleteAccountConfirm: 'Supprimer mon compte' },
    dashboard: {
      ...(resources.en.translation.dashboard || {}),
      welcomeBack: 'Bon retour',
      continueApplication: '▶ Continuer la demande',
      yourTodoList: 'Votre liste de tâches',
      yourSpecialist: 'Votre spécialiste',
      nav: { ...(resources.en.translation.dashboard?.nav || {}), home: 'Accueil', application: 'Demande', support: 'Support', profile: 'Profil', global: 'Global' },
    },
    chat: {
      ...(resources.en.translation.chat || {}),
      title: 'Chat de la demande',
      backToDashboard: 'Retour au tableau de bord',
      talkToHuman: 'Parler à un humain',
      viewToggle: 'Basculer entre chat et formulaire',
      viewChat: 'Chat',
      viewForm: 'Formulaire',
      placeholder: 'Écrivez votre message…',
      errorSendingMessage: "Erreur lors de l'envoi du message. Veuillez réessayer.",
    },
    landing: {
      ...(resources.en.translation.landing || {}),
      eyebrow: 'ESPAGNE · RÉGULARISATION EXTRAORDINAIRE 2026',
      title1: 'Pouvez-vous obtenir',
      title2: 'la résidence',
      title3: 'légale',
      title4: 'en',
      titleYear: '2026 ?',
      intro1: "Si vous êtes déjà en Espagne, c’est peut-être votre chance d’obtenir une résidence légale. Nous vous aidons à le faire correctement.",
      intro2: 'La période se termine le <strong>30 juin 2026</strong>. Si vous attendez - ou faites une erreur - vous pourriez perdre cette opportunité.',
      intro3: 'Avec nous - du premier coup, sans erreurs.',
      ctaMain: 'Vérifier si je suis éligible',
      ctaSubline: '{{price}} tout compris · vous payez uniquement lors du dépôt',
      ctaNote: 'Aucun paiement à l’avance · facturé uniquement lorsque votre demande est déposée',
      attorneySection: 'VOTRE AVOCATE',
      attorneyRole: 'Avocate en immigration · Espagne',
      attorneyStats: '10+ ans d’expérience · 1 500+ dossiers traités',
      attorneyFirmBadge: "Cabinet d’immigration Travieso",
      quote:
        "J’ai traité plus de 1 500 dossiers d’immigration en Espagne. Ce que je sais, c’est que la plupart des refus ne viennent pas d’un manque d’éligibilité - mais de documents incorrects ou incomplets. C’est exactement ce que nous évitons. Si vous êtes éligible, je veux m’assurer qu’une erreur de dossier ne vous fasse pas perdre votre opportunité.",
      available: 'Disponible',
      hideCreds: 'Masquer les qualifications',
      viewCreds: 'Voir les qualifications',
      whyTitle: 'Pourquoi',
      whyAccent: 'Global Mobility',
      why1Title: 'Une personne réelle pour votre dossier',
      why1Desc: 'Un spécialiste dédié vous est attribué dès le premier jour, connaît votre dossier et est disponible quand vous en avez besoin.',
      why2Title: 'Aucun paiement à l’avance - vous payez seulement au dépôt',
      why2Desc: 'Tarif fixe de 250 €, facturé uniquement lorsque votre demande est déposée.',
      why3Title: 'Chaque demande est revue par un avocat',
      why3Desc: 'Avant le dépôt, un avocat spécialisé en immigration vérifie chaque détail de votre dossier.',
      why4Title: 'Nous nous occupons de tout pour vous',
      why4Desc1: 'Du premier document au dépôt final -',
      why4Desc2: 'vous n’avez pas à gérer le portail gouvernemental.',
      why4Desc3: 'On s’en charge.',
      why5Title: 'Conçu pour éviter les erreurs',
      why5Desc: 'Nous combinons expérience juridique et processus de vérification structuré pour corriger les problèmes avant le dépôt.',
      howTitle: 'Comment ça',
      howAccent: 'marche',
      step1Title: 'Démarrer (2 minutes)',
      step1Desc: 'Un spécialiste vous contactera sur WhatsApp sous 24 h - aucun papier pour le moment.',
      step2Title: 'Accompagnement pas à pas',
      step2Desc: 'Un spécialiste dédié vous aide à rassembler et organiser vos documents.',
      step3Title: 'Revue juridique',
      step3Desc: 'Un avocat spécialisé en immigration vérifie votre dossier complet <strong>avant le dépôt.</strong>',
      step4Title: 'Dépôt et suivi',
      step4Desc: 'Nous déposons votre dossier et vous tenons informé jusqu’à la décision.',
      pricingTitle: 'Tarifs',
      allInclusive: 'TOUT COMPRIS',
      perPerson: 'Par personne',
      noUpfront: 'Aucun paiement à l’avance - pas un euro avant le dépôt',
      successFee: 'Facturé uniquement lorsque votre dossier est complet et déposé. Sans surprise.',
      whatIncludes: 'Ce qui est inclus',
      priceList1: 'Spécialiste dédié dès le premier jour',
      priceList2: 'Revue complète par un avocat avant dépôt',
      priceList3: 'Mises à jour jusqu’à la décision finale',
      ctaBoxTitle: 'Ne manquez pas votre chance de régulariser votre situation en Espagne.',
      ctaBoxDesc: 'La fenêtre est limitée - et les erreurs peuvent vous coûter cette opportunité.',
      ctaBoxButton: "Vérifier mon éligibilité - c'est gratuit",
      ctaFreeNote: "C'est gratuit - sans engagement",
      footer: {
        ...(resources.en.translation.landing?.footer || {}),
        companyLine1: 'Global Mobility est un nom commercial de',
        companyLine2: 'Représentante UE : Ana Maria Travieso Hernandez -',
        linkTerms: "Conditions d’utilisation",
        linkPrivacy: 'Politique de confidentialité',
        linkCookies: 'Politique de cookies',
        linkLegal: 'Mentions légales',
      },
    },
    signup: {
      ...(resources.en.translation.signup || {}),
      signIn: 'SE CONNECTER',
      newAccount: 'Nouveau compte',
      mobileTitle: 'D’abord, entrez<br />votre <em>numéro de mobile</em>',
      mobileTitleSignIn: 'Entrez votre <em>mobile</em> pour vous connecter',
      mobileDesc:
        'Vos informations sont utilisées uniquement pour gérer votre demande et sont protégées par notre <privacy>Politique de confidentialité</privacy>.',
      continue: 'Continuer',
      or: 'ou',
      secondTitle: 'Ensuite, avez-vous <em>WhatsApp ?</em>',
      secondDesc: 'Choisissez comment vous souhaitez recevoir les mises à jour, documents et messages de votre conseiller(ère).',
      whatsappDesc: 'Recevez les documents et mises à jour directement sur WhatsApp.',
      smsTitle: 'Mobile (SMS)',
      smsDesc: 'Messages texte standard. Fonctionne sans internet.',
      tip: 'Vous pouvez modifier votre canal préféré à tout moment dans les paramètres de votre compte.',
      sending: 'Envoi…',
      otpTitle: 'Entrez le code que nous avons envoyé',
      otpDesc: 'Nous avons envoyé un code à usage unique à votre {{channel}}.',
      otpPlaceholder: 'Entrez le code à 6 chiffres',
      verifying: 'Vérification…',
      otpResendTimerPrefix: 'Renvoyer dans ',
      otpResendReady: 'Vous pouvez renvoyer le code maintenant.',
      otpResendCode: 'Renvoyer le code',
      terms:
        'En continuant, vous acceptez nos <terms>Conditions d’utilisation</terms>, notre <privacy>Politique de confidentialité</privacy> et notre <dpa>Accord de traitement des données</dpa>.',
      noAccount: 'Vous n’avez pas de compte ?',
      signUpCta: "S’inscrire →",
      haveAccount: 'Vous avez déjà un compte ?',
      signInCta: 'Se connecter →',
      errors: {
        ...(resources.en.translation.signup?.errors || {}),
        invalidPhone: 'Veuillez saisir un numéro de téléphone valide.',
        numberAlreadyRegistered: 'Ce numéro est déjà enregistré. Veuillez vous connecter.',
        enterCode: 'Veuillez saisir le code reçu.',
        invalidOtp: 'Code invalide ou expiré.',
        sendFailed: "Échec de l'envoi du code. Réessayez.",
      },
    },
    qualify: {
      ...(resources.en.translation.qualify || {}),
      eyebrow: "Vérification d'éligibilité",
      badge: 'Moins de 1 min',
      title: 'Vérifiez si vous <accent>êtes éligible</accent>',
      subtitle: "Répondez à quelques questions oui/non pour savoir si vous êtes éligible à la Régularisation Extraordinaire 2026 de l'Espagne.",
      yes: 'Oui',
      no: 'Non',
      q1: 'Êtes-vous arrivé(e) en Espagne avant le 1er janvier 2026 ?',
      q2: "Avez-vous demandé l'asile en Espagne avant le 1er janvier 2026 ?",
      q3: 'Avez-vous été condamné pour un crime en Espagne ?',
      q4: 'Votre casier judiciaire a-t-il déjà été effacé ?',
      q5: "Pensez-vous que le délai légal d'effacement est déjà dépassé ?",
      altTitle: 'Explorons <accent>d’autres options</accent>',
      altDesc:
        "D’après vos réponses, vous ne remplissez peut-être pas les conditions pour la Régularisation Extraordinaire 2026. Cependant, d’autres options légales peuvent être possibles. Continuez pour vous inscrire, et un spécialiste examinera votre dossier pour déterminer la meilleure démarche.",
      continue: 'Continuer',
      startOver: 'Recommencer',
      qualifiedPrivacy:
        'Vos réponses sont confidentielles et protégées par notre <privacy>Politique de confidentialité</privacy>.',
      qualifiedBadge: 'Éligibilité confirmée',
      qualifiedTitle: 'Bonne nouvelle - vous êtes éligible à la <accent>Régularisation Extraordinaire 2026.</accent>',
      qualifiedSub: "Allons-y. Notre équipe vous guidera à chaque étape - des documents au dépôt final.",
    },
    welcomePage: {
      ...(resources.en.translation.welcomePage || {}),
      welcomeBadge: 'Bon retour',
      title: 'Votre portail est prêt',
      body:
        'Bon retour. Votre portail client sécurisé est prêt : vous pouvez tout compléter à votre rythme et téléverser des documents depuis votre téléphone. Cela accélère votre dossier et réduit les erreurs. Cliquez ci-dessous pour continuer.',
      next1: 'Vos informations sont sécurisées dans notre système',
      next2: 'Un spécialiste examine votre dossier',
      next3: 'Nous vous contacterons par téléphone, WhatsApp ou e-mail',
      next4: 'Si plus de 48 heures se sont écoulées, écrivez-nous - nous répondrons rapidement',
      footnote: "Nous vous contacterons uniquement à propos de votre dossier",
      primaryCta: 'Nous écrire sur WhatsApp',
      backLink: "Retour à l’accueil",
    },
    ssoSignup: {
      ...(resources.en.translation.ssoSignup || {}),
      newAccount: 'Nouveau compte',
      step2of2: 'Étape 2 sur 2',
      step3of3: 'Étape 3 sur 3',
      takesLessThanMinute: 'Moins de 1 minute',
      almostDone: 'Presque terminé',
      detailsTitle: '<accent>Commençons</accent>',
      detailsTitlePlain: 'Commençons',
      detailsSubtitle: 'Quelques informations rapides',
      detailsContinueTerms:
        'En continuant, vous acceptez nos <terms>Conditions d’utilisation</terms>, notre <privacy>Politique de confidentialité</privacy> et notre <dpa>Accord de traitement des données</dpa>.',
      firstName: 'Prénom',
      lastName: 'Nom',
      forQuestion: 'Cette demande est pour qui ?',
      forMe: 'Pour moi',
      forSomeoneElse: 'Pour quelqu’un d’autre',
      familyQuestion: 'Vous postulez seul(e) ou en famille ?',
      justMe: 'Juste moi',
      forMyFamily: 'Pour ma famille',
      howManyPeople: 'Combien de personnes ?',
      includingYourself: 'Vous compris',
      continue: 'Continuer',
      noDocsNeededShort: 'Aucun document requis pour le moment.',
      guideEverythingShort: 'Nous vous guiderons à chaque étape.',
      contactTitle: 'Comment devons-nous <accent>vous contacter</accent> ?',
      contactSubtitle: 'Nous utiliserons ces informations pour vous envoyer des mises à jour sur votre dossier',
      phonePreferred: 'Votre téléphone (préféré)',
      contactBy: 'Nous vous contacterons par WhatsApp, SMS ou e-mail',
      sendCode: 'Envoyer le code',
      resend: 'Renvoyer',
      verified: 'Vérifié',
      enterCode: 'Saisissez le code à 6 chiffres',
      incorrectCode: 'Code incorrect. Veuillez réessayer.',
      resendCode: 'Renvoyer le code',
      inSeconds: 'dans {{seconds}} s',
      verifyEmailOptional: 'Votre e-mail',
      optional: '(optionnel)',
      updatesOrDocs: 'Nous pouvons y envoyer des mises à jour importantes ou des documents',
      emailAsMain: "Utiliser l’e-mail comme contact principal à la place du mobile",
      continueNextSteps: 'Continuer vers les prochaines étapes',
      saving: 'Enregistrement…',
      errors: {
        nameRequired: 'Le prénom et le nom sont obligatoires.',
        forRequired: 'Sélectionnez pour qui est cette demande.',
        familyRequired: 'Sélectionnez si vous postulez seul(e) ou en famille.',
        saveFailed: "Impossible d’enregistrer. Veuillez réessayer.",
      },
      onlyCaseContact: 'Nous vous contactons uniquement au sujet de votre dossier',
    },
    successPage: {
      ...(resources.en.translation.successPage || {}),
      brandTitle: 'Global Mobility',
      brandSubtitle: 'Régularisation Espagne 2026',
      completedBadge: 'Inscription terminée',
      title: 'Tout est prêt !',
      body: 'Nous vous contacterons sous 24-48 heures pour la suite.',
      next1: 'Nous vérifions vos informations',
      next2: 'Nous vous contactons par téléphone ou e-mail',
      next3: 'Nous vous guidons pour les prochaines étapes',
      next4: 'Vous pouvez déjà préparer : passeport, preuve de présence en Espagne et documents justificatifs',
      referral: {
        ...(resources.en.translation.successPage?.referral || {}),
        ariaLabel: 'Vous avez un code de parrainage ?',
        title: 'Vous avez un code de parrainage ?',
        subtitle: "Optionnel - si quelqu'un vous a parrainé, saisissez son code ici",
        apply: 'Appliquer',
        applying: 'Application...',
        applied: 'Appliqué',
        appliedMessage: 'Code de parrainage appliqué avec succès.',
        applyError: "Impossible d'appliquer le code de parrainage.",
      },
      backHome: 'Retour en haut',
      footer: 'Nous vous contacterons uniquement au sujet de votre dossier',
      companyLine1: 'Global Mobility est un nom commercial de',
      companyName: 'Global Mobility Group LLC',
      companyAddress: '140 E Broadway Ave, Suite 25, Jackson, WY 83001, USA',
      companyLine2: 'Représentante UE : Ana Maria Travieso Hernandez -',
      linkTerms: "Conditions d’utilisation",
      linkPrivacy: 'Politique de confidentialité',
      linkCookies: 'Politique de cookies',
      linkLegal: 'Mentions légales',
      copyright: '© 2026 Global Mobility Group LLC',
    },
  },
};

resources.ar = {
  translation: {
    ...resources.en.translation,
    cookie: {
      ...(resources.en.translation.cookie || {}),
      title: 'إشعار ملفات تعريف الارتباط',
      text: 'نستخدم ملفات تعريف الارتباط لتقديم الخدمة وتحسين تجربتك. بالمتابعة، فإنك توافق على استخدامنا لملفات تعريف الارتباط.',
      accept: 'موافقة',
    },
    legal: {
      ...(resources.en.translation.legal || {}),
      privacy: 'الخصوصية',
      cookiePolicy: 'سياسة ملفات تعريف الارتباط',
    },
    loading: 'جارٍ التحميل…',
    signingIn: 'جارٍ تسجيل الدخول…',
    logOut: 'تسجيل الخروج',
    back: 'رجوع',
    settings: { ...(resources.en.translation.settings || {}), title: 'الإعدادات', preferences: 'التفضيلات', language: 'اللغة' },
    account: { ...(resources.en.translation.account || {}), title: 'الحساب', settings: 'إعدادات الحساب', editProfile: 'تعديل الملف الشخصي', actions: 'إجراءات', deleteAccount: 'حذف الحساب', deleteAccountTitle: 'حذف الحساب؟', deleteAccountWarning: 'سيؤدي هذا إلى حذف حسابك وجميع بياناتك نهائيًا. لا يمكن التراجع عن ذلك.', deleteAccountConfirm: 'حذف حسابي' },
    dashboard: {
      ...(resources.en.translation.dashboard || {}),
      welcomeBack: 'مرحبًا بعودتك',
      continueApplication: '▶ متابعة الطلب',
      yourTodoList: 'قائمة المهام',
      yourSpecialist: 'أخصائيّك',
      nav: { ...(resources.en.translation.dashboard?.nav || {}), home: 'الرئيسية', application: 'الطلب', support: 'الدعم', profile: 'الملف الشخصي', global: 'عام' },
    },
    chat: {
      ...(resources.en.translation.chat || {}),
      title: 'دردشة الطلب',
      backToDashboard: 'العودة إلى لوحة التحكم',
      talkToHuman: 'التحدث إلى شخص',
      viewToggle: 'التبديل بين الدردشة والنموذج',
      viewChat: 'الدردشة',
      viewForm: 'النموذج',
      placeholder: 'اكتب رسالتك…',
      errorSendingMessage: 'حدث خطأ أثناء إرسال الرسالة. حاول مرة أخرى.',
    },
    landing: {
      ...(resources.en.translation.landing || {}),
      eyebrow: 'إسبانيا · تسوية استثنائية 2026',
      title1: 'هل يمكنك الحصول على',
      title2: 'إقامة',
      title3: 'قانونية',
      title4: 'في',
      titleYear: '2026؟',
      intro1: 'إذا كنت بالفعل في إسبانيا، فقد تكون هذه فرصتك للحصول على إقامة قانونية. سنساعدك على القيام بذلك بشكل صحيح.',
      intro2: 'تنتهي المدة في <strong>30 يونيو 2026</strong>. إذا انتظرت - أو ارتكبت خطأ - فقد تفقد هذه الفرصة.',
      intro3: 'اعمل معنا - صحيحًا من المرة الأولى.',
      ctaMain: 'تحقق إن كنت مؤهلاً',
      ctaSubline: '{{price}} شاملة · تدفع فقط عند التقديم',
      ctaNote: 'لا دفعات مسبقة · يتم الخصم فقط عند تقديم طلبك',
      attorneySection: 'محاميتك',
      attorneyRole: 'محامية هجرة · إسبانيا',
      attorneyStats: 'خبرة +10 سنوات · +1500 قضية',
      quote:
        'لقد تعاملتُ مع أكثر من 1500 قضية هجرة في إسبانيا. ما أعرفه هو أن معظم الرفض لا يكون بسبب عدم الأهلية - بل بسبب مستندات غير صحيحة أو غير مكتملة. هذا بالضبط ما نمنعه. إذا كنت مؤهلاً، أريد أن أضمن ألا تحرمك هفوة في الأوراق من فرصتك.',
      available: 'متاحة',
      hideCreds: 'إخفاء المؤهلات',
      viewCreds: 'عرض المؤهلات',
      whyTitle: 'لماذا',
      whyAccent: 'Global Mobility',
      why1Title: 'شخص حقيقي يتابع ملفك',
      why1Desc: 'يتم تعيين مختص لك من اليوم الأول يعرف ملفك ويكون متاحًا عند الحاجة.',
      why2Title: 'بدون دفع مسبق - تدفع فقط عند التقديم',
      why2Desc: 'رسوم ثابتة 250€ تُخصم فقط عند تقديم طلبك.',
      why3Title: 'مراجعة قانونية لكل طلب',
      why3Desc: 'قبل التقديم، يراجع محامٍ مختص تفاصيل ملفك بالكامل.',
      why4Title: 'نقوم بكل شيء نيابةً عنك',
      why4Desc1: 'من أول مستند حتى التقديم النهائي -',
      why4Desc2: 'لن تحتاج للتعامل مع بوابة الحكومة.',
      why4Desc3: 'نحن نتولى ذلك.',
      why5Title: 'مصمم لتجنب الأخطاء',
      why5Desc: 'نجمع الخبرة القانونية مع عملية مراجعة منظمة لاكتشاف الأخطاء وإصلاحها قبل التقديم.',
      howTitle: 'كيف',
      howAccent: 'يعمل',
      step1Title: 'ابدأ (دقيقتان)',
      step1Desc: 'سيتواصل معك مختص عبر واتساب خلال 24 ساعة - بدون أوراق الآن.',
      step2Title: 'إرشاد خطوة بخطوة',
      step2Desc: 'مختص مخصص يساعدك في جمع وتنظيم المستندات.',
      step3Title: 'مراجعة قانونية',
      step3Desc: 'يراجع محامٍ مختص ملفك بالكامل <strong>قبل التقديم.</strong>',
      step4Title: 'التقديم والمتابعة',
      step4Desc: 'نقدم ملفك ونبقيك على اطلاع حتى صدور القرار.',
      pricingTitle: 'السعر',
      allInclusive: 'شامل',
      perPerson: 'للشخص',
      noUpfront: 'بدون دفع مسبق - لا يورو قبل التقديم',
      successFee: 'يتم الخصم فقط عند اكتمال طلبك وتقديمه. بدون مفاجآت.',
      whatIncludes: 'ما الذي يتضمنه',
      priceList1: 'مختص مخصص من اليوم الأول',
      priceList2: 'مراجعة محامٍ قبل التقديم',
      priceList3: 'تحديثات حتى القرار النهائي',
      ctaBoxTitle: 'لا تفوّت فرصتك للحصول على إقامة قانونية في إسبانيا.',
      ctaBoxDesc: 'الوقت محدود - والأخطاء قد تكلفك هذه الفرصة.',
      ctaBoxButton: 'تحقق إن كنت مؤهلاً - مجانًا',
      ctaFreeNote: 'مجاني - بدون التزام',
      footer: {
        ...(resources.en.translation.landing?.footer || {}),
        companyLine1: 'Global Mobility هو اسم تجاري لـ',
        companyLine2: 'ممثل الاتحاد الأوروبي: Ana Maria Travieso Hernandez -',
        linkTerms: 'شروط الخدمة',
        linkPrivacy: 'سياسة الخصوصية',
        linkCookies: 'سياسة ملفات تعريف الارتباط',
        linkLegal: 'إشعار قانوني',
      },
    },
    signup: {
      ...(resources.en.translation.signup || {}),
      signIn: 'تسجيل الدخول',
      newAccount: 'حساب جديد',
      mobileTitle: 'أولاً، أدخل<br />رقم <em>هاتفك</em>',
      mobileTitleSignIn: 'أدخل <em>هاتفك</em> لتسجيل الدخول',
      mobileDesc:
        'تُستخدم معلوماتك فقط لإدارة طلبك وهي محمية بموجب <privacy>سياسة الخصوصية</privacy> الخاصة بنا.',
      continue: 'متابعة',
      or: 'أو',
      secondTitle: 'ثانيًا، هل لديك <em>واتساب؟</em>',
      secondDesc: 'اختر كيف ترغب في تلقي التحديثات والوثائق والرسائل من مستشارك.',
      whatsappDesc: 'استلام الوثائق والتحديثات مباشرة عبر واتساب.',
      smsTitle: 'الهاتف (SMS)',
      smsDesc: 'رسائل نصية عادية. تعمل بدون إنترنت.',
      tip: 'يمكنك تغيير قناة التواصل المفضلة في أي وقت من إعدادات حسابك.',
      sending: 'جارٍ الإرسال…',
      otpTitle: 'أدخل الرمز الذي أرسلناه',
      otpDesc: 'أرسلنا رمزًا لمرة واحدة إلى {{channel}}.',
      otpPlaceholder: 'أدخل رمزًا من 6 أرقام',
      verifying: 'جارٍ التحقق…',
      otpResendTimerPrefix: 'إعادة الإرسال خلال ',
      otpResendReady: 'يمكنك إعادة إرسال الرمز الآن.',
      otpResendCode: 'إعادة إرسال الرمز',
      terms:
        'بالمتابعة، فإنك توافق على <terms>شروط الخدمة</terms> و<privacy>سياسة الخصوصية</privacy> و<dpa>اتفاقية معالجة البيانات</dpa> الخاصة بنا.',
      noAccount: 'ليس لديك حساب؟',
      signUpCta: 'أنشئ حسابًا →',
      haveAccount: 'لديك حساب بالفعل؟',
      signInCta: 'سجّل الدخول →',
      errors: {
        ...(resources.en.translation.signup?.errors || {}),
        invalidPhone: 'يرجى إدخال رقم هاتف صحيح.',
        numberAlreadyRegistered: 'هذا الرقم مسجّل بالفعل. يرجى تسجيل الدخول.',
        enterCode: 'يرجى إدخال الرمز الذي تلقيته.',
        invalidOtp: 'رمز غير صحيح أو منتهي.',
        sendFailed: 'فشل إرسال الرمز. حاول مرة أخرى.',
      },
    },
    qualify: {
      ...(resources.en.translation.qualify || {}),
      eyebrow: 'التحقق من الأهلية',
      badge: 'أقل من دقيقة',
      title: 'تحقق إن كنت <accent>مؤهلاً</accent>',
      subtitle: 'أجب عن أسئلة سريعة بنعم/لا لمعرفة ما إذا كنت مؤهلاً لمسار تسوية إسبانيا 2026.',
      yes: 'نعم',
      no: 'لا',
      q1: 'هل وصلت إلى إسبانيا قبل 1 يناير 2026؟',
      q2: 'هل سبق أن تقدّمت بطلب لجوء في إسبانيا؟',
      q3: 'هل أُدينت بجريمة في إسبانيا؟',
      q4: 'هل تم محو سجلاتك الجنائية بالفعل؟',
      q5: 'هل تعتقد أن المدة القانونية قد انقضت ويمكن الآن محو السجل؟',
      altTitle: 'لنستكشف <accent>خيارات أخرى</accent>',
      altDesc:
        'استنادًا إلى إجاباتك، قد لا تكون مؤهلاً للتسوية الاستثنائية لعام 2026. ومع ذلك، قد تكون هناك خيارات قانونية أخرى. تابع للتسجيل وسيقوم مختص بمراجعة حالتك لتحديد أفضل مسار.',
      continue: 'متابعة',
      startOver: 'ابدأ من جديد',
      qualifiedPrivacy:
        'إجاباتك سرّية ومحمية بموجب <privacy>سياسة الخصوصية</privacy> الخاصة بنا.',
      qualifiedBadge: 'تم تأكيد الأهلية',
      qualifiedTitle: 'خبر سار - أنت مؤهل لـ <accent>التسوية الاستثنائية 2026.</accent>',
      qualifiedSub: 'لنبدأ. سيرشدك فريقنا في كل خطوة - من الوثائق حتى التقديم النهائي.',
    },
    welcomePage: {
      ...(resources.en.translation.welcomePage || {}),
      welcomeBadge: 'مرحبًا بعودتك',
      title: 'بوابتك جاهزة',
      body:
        'مرحبًا بعودتك. بوابتك الآمنة للعميل جاهزة، حيث يمكنك إكمال كل شيء بالوتيرة التي تناسبك ورفع المستندات من هاتفك. هذا يسرّع حالتك ويقلّل الأخطاء. اضغط أدناه للمتابعة.',
      next1: 'معلوماتك محفوظة بأمان في نظامنا',
      next2: 'يقوم مختص بمراجعة حالتك',
      next3: 'سنتواصل معك عبر الهاتف أو واتساب أو البريد الإلكتروني',
      next4: 'إذا مرّت أكثر من 48 ساعة، راسلنا - سنرد بسرعة',
      footnote: 'سنتواصل معك فقط بخصوص حالتك',
      primaryCta: 'راسلنا على واتساب',
      backLink: 'العودة إلى الصفحة الرئيسية',
    },
    ssoSignup: {
      ...(resources.en.translation.ssoSignup || {}),
      newAccount: 'حساب جديد',
      step2of2: 'الخطوة 2 من 2',
      step3of3: 'الخطوة 3 من 3',
      takesLessThanMinute: 'أقل من دقيقة',
      almostDone: 'على وشك الانتهاء',
      detailsTitle: '<accent>لنبدأ</accent>',
      detailsTitlePlain: 'لنبدأ',
      detailsSubtitle: 'بعض التفاصيل السريعة',
      detailsContinueTerms:
        'بالمتابعة، فإنك توافق على <terms>شروط الخدمة</terms> و<privacy>سياسة الخصوصية</privacy> و<dpa>اتفاقية معالجة البيانات</dpa> الخاصة بنا.',
      firstName: 'الاسم الأول',
      lastName: 'اسم العائلة',
      forQuestion: 'هذه الطلبات لمن؟',
      forMe: 'لي',
      forSomeoneElse: 'لشخص آخر',
      familyQuestion: 'هل تتقدم وحدك أم مع عائلتك؟',
      justMe: 'أنا فقط',
      forMyFamily: 'لعائلتي',
      howManyPeople: 'كم عدد الأشخاص؟',
      includingYourself: 'بما فيهم أنت',
      continue: 'متابعة',
      noDocsNeededShort: 'لا حاجة لوثائق في هذه المرحلة.',
      guideEverythingShort: 'سنرشدك في كل شيء.',
      contactTitle: 'كيف يجب أن <accent>نتواصل معك؟</accent>',
      contactSubtitle: 'سنستخدم هذه المعلومات لإرسال تحديثات حول حالتك',
      phonePreferred: 'رقم هاتفك (المفضل)',
      contactBy: 'سنتواصل معك عبر واتساب أو رسالة نصية أو البريد الإلكتروني',
      sendCode: 'إرسال الرمز',
      resend: 'إعادة الإرسال',
      verified: 'تم التحقق',
      enterCode: 'أدخل الرمز المكوّن من 6 أرقام',
      incorrectCode: 'الرمز غير صحيح. حاول مرة أخرى.',
      resendCode: 'إعادة إرسال الرمز',
      inSeconds: 'خلال {{seconds}}ث',
      verifyEmailOptional: 'بريدك الإلكتروني',
      optional: '(اختياري)',
      updatesOrDocs: 'قد نرسل تحديثات مهمة أو مستندات هنا',
      emailAsMain: 'استخدام البريد الإلكتروني كوسيلة التواصل الرئيسية بدل الهاتف',
      continueNextSteps: 'المتابعة إلى الخطوات التالية',
      saving: 'جارٍ الحفظ…',
      errors: {
        nameRequired: 'الاسم الأول واسم العائلة مطلوبان.',
        forRequired: 'اختر لمن هذا الطلب.',
        familyRequired: 'اختر هل تتقدم وحدك أم مع العائلة.',
        saveFailed: 'تعذر الحفظ. حاول مرة أخرى.',
      },
      onlyCaseContact: 'نتواصل معك فقط بخصوص حالتك',
    },
    successPage: {
      ...(resources.en.translation.successPage || {}),
      brandTitle: 'Global Mobility',
      brandSubtitle: 'تسوية إسبانيا 2026',
      completedBadge: 'اكتمل التسجيل',
      title: 'كل شيء جاهز!',
      body: 'سنتواصل معك خلال 24-48 ساعة للخطوات التالية.',
      next1: 'نراجع معلوماتك',
      next2: 'نتواصل معك عبر الهاتف أو البريد الإلكتروني',
      next3: 'نرشدك للخطوات التالية',
      next4: 'يمكنك البدء بالتحضير: جواز السفر، إثبات المدة في إسبانيا، والوثائق الداعمة',
      referral: {
        ...(resources.en.translation.successPage?.referral || {}),
        ariaLabel: 'هل لديك رمز إحالة؟',
        title: 'هل لديك رمز إحالة؟',
        subtitle: 'اختياري - إذا قام أحد بإحالتك، أدخل الرمز هنا',
        apply: 'تطبيق',
        applying: 'جارٍ التطبيق...',
        applied: 'تم التطبيق',
        appliedMessage: 'تم تطبيق رمز الإحالة بنجاح.',
        applyError: 'تعذر تطبيق رمز الإحالة.',
      },
      backHome: 'العودة إلى الأعلى',
      footer: 'سنتواصل معك فقط بخصوص حالتك',
      companyLine1: 'Global Mobility هو اسم تجاري لـ',
      companyName: 'Global Mobility Group LLC',
      companyAddress: '140 E Broadway Ave, Suite 25, Jackson, WY 83001, USA',
      companyLine2: 'ممثل الاتحاد الأوروبي: Ana Maria Travieso Hernandez -',
      linkTerms: 'شروط الخدمة',
      linkPrivacy: 'سياسة الخصوصية',
      linkCookies: 'سياسة ملفات تعريف الارتباط',
      linkLegal: 'إشعار قانوني',
      copyright: '© 2026 Global Mobility Group LLC',
    },
  },
};

resources.zh = {
  translation: {
    ...resources.en.translation,
    cookie: {
      ...(resources.en.translation.cookie || {}),
      title: 'Cookie（曲奇）提示',
      text: '我们使用 Cookie（曲奇）来提供服务并改善体验。继续使用即表示你同意我们使用 Cookie（曲奇）。',
      accept: '接受',
    },
    legal: {
      ...(resources.en.translation.legal || {}),
      privacy: '隐私',
      cookiePolicy: 'Cookie（曲奇）政策',
    },
    loading: '加载中…',
    signingIn: '正在登录…',
    logOut: '退出登录',
    back: '返回',
    settings: { ...(resources.en.translation.settings || {}), title: '设置', preferences: '偏好设置', language: '语言' },
    account: { ...(resources.en.translation.account || {}), title: '账户', settings: '账户设置', editProfile: '编辑资料', actions: '操作', deleteAccount: '删除账户', deleteAccountTitle: '删除账户？', deleteAccountWarning: '这将永久删除你的账户和所有数据，且无法撤销。', deleteAccountConfirm: '删除我的账户' },
    dashboard: {
      ...(resources.en.translation.dashboard || {}),
      welcomeBack: '欢迎回来',
      continueApplication: '▶ 继续申请',
      yourTodoList: '待办事项',
      yourSpecialist: '你的专员',
      nav: { ...(resources.en.translation.dashboard?.nav || {}), home: '首页', application: '申请', support: '支持', profile: '个人资料', global: '全局' },
    },
    chat: {
      ...(resources.en.translation.chat || {}),
      title: '申请聊天',
      backToDashboard: '返回仪表板',
      talkToHuman: '联系人工',
      viewToggle: '在聊天与表单之间切换',
      viewChat: '聊天',
      viewForm: '表单',
      placeholder: '输入消息…',
      errorSendingMessage: '消息发送失败。请重试。',
    },
    landing: {
      ...(resources.en.translation.landing || {}),
      eyebrow: '西班牙 · 2026 特别正规化',
      title1: '你能获得',
      title2: '合法',
      title3: '居留',
      title4: '在',
      titleYear: '2026 吗？',
      intro1: '如果你已经在西班牙，这可能是你获得合法居留的机会。我们会帮助你正确办理。',
      intro2: '窗口期截止 <strong>2026年6月30日</strong>。如果等待--或出错--你可能会失去机会。',
      intro3: '与我们合作--一次做对。',
      ctaMain: '查看我是否符合资格',
      ctaSubline: '{{price}} 全包 · 仅在提交时付款',
      ctaNote: '无需预付 · 仅在提交申请时扣款',
      attorneySection: '你的律师',
      attorneyRole: '移民律师 · 西班牙',
      attorneyStats: '10年以上经验 · 处理1500+案件',
      quote:
        '我在西班牙处理过超过 1,500 起移民案件。我知道：大多数被拒并不是因为不符合资格--而是因为材料不正确或不完整。这正是我们要避免的。如果你符合资格，我希望确保不会因为文书上的小错误而错失机会。',
      available: '在线',
      hideCreds: '隐藏资质',
      viewCreds: '查看资质',
      whyTitle: '为什么选择',
      whyAccent: 'Global Mobility',
      why1Title: '专人负责你的案件',
      why1Desc: '从第一天起为你分配专属专员，了解你的材料并在你需要时提供支持。',
      why2Title: '无需预付--仅在提交时付款',
      why2Desc: '固定费用 €250，仅在你的申请提交时收取。',
      why3Title: '每份申请都有律师审核',
      why3Desc: '在提交前，由持牌移民律师逐项审核你的材料与信息。',
      why4Title: '我们替你处理所有流程',
      why4Desc1: '从第一份文件到最终提交--',
      why4Desc2: '你无需面对政府门户网站。',
      why4Desc3: '我们来处理。',
      why5Title: '为避免错误而设计',
      why5Desc: '结合真实法律经验与结构化审核流程，提前发现并修复问题，避免被拒。',
      howTitle: '如何',
      howAccent: '运作',
      step1Title: '开始（2 分钟）',
      step1Desc: '专员将在 24 小时内通过 WhatsApp 联系你--暂时无需提交材料。',
      step2Title: '一步步指导',
      step2Desc: '专属专员协助你收集并整理所需文件。',
      step3Title: '法律审核',
      step3Desc: '移民律师会在提交前 <strong>审核你的完整申请。</strong>',
      step4Title: '提交与跟进',
      step4Desc: '我们提交你的案件并持续更新进展，直到最终结果。',
      pricingTitle: '价格',
      allInclusive: '全包',
      perPerson: '每人',
      noUpfront: '无需预付--提交前不收取任何费用',
      successFee: '仅在资料完整并提交后收费。无隐藏费用。',
      whatIncludes: '包含内容',
      priceList1: '从第一天起的专属专员',
      priceList2: '提交前的律师审核',
      priceList3: '直到最终决定的进度更新',
      ctaBoxTitle: '不要错过在西班牙合法化的机会。',
      ctaBoxDesc: '窗口期有限--错误可能让你失去机会。',
      ctaBoxButton: '查看我是否符合资格--免费',
      ctaFreeNote: '免费--无承诺',
      footer: {
        ...(resources.en.translation.landing?.footer || {}),
        companyLine1: 'Global Mobility 是以下公司的商号：',
        companyLine2: '欧盟代表：Ana Maria Travieso Hernandez -',
        linkTerms: '服务条款',
        linkPrivacy: '隐私政策',
        linkCookies: 'Cookie 政策',
        linkLegal: '法律声明',
      },
    },
    signup: {
      ...(resources.en.translation.signup || {}),
      signIn: '登录',
      newAccount: '新账户',
      mobileTitle: '首先，输入<br />你的<em>手机号</em>',
      mobileTitleSignIn: '输入你的<em>手机号</em>以登录',
      mobileDesc:
        '你的信息仅用于管理你的申请，并受我们的<privacy>隐私政策</privacy>保护。',
      continue: '继续',
      or: '或',
      secondTitle: '其次，你有 <em>WhatsApp</em> 吗？',
      secondDesc: '选择你希望如何接收来自专员的更新、文件和消息。',
      whatsappDesc: '通过 WhatsApp 直接接收文件和更新。',
      smsTitle: '手机（短信）',
      smsDesc: '标准短信。无需互联网也可使用。',
      tip: '你可以随时在账户设置中更改首选渠道。',
      sending: '发送中…',
      otpTitle: '输入我们发送的验证码',
      otpDesc: '我们已向你的 {{channel}} 发送一次性验证码。',
      otpPlaceholder: '输入6位验证码',
      verifying: '验证中…',
      otpResendTimerPrefix: '重新发送倒计时 ',
      otpResendReady: '你现在可以重新发送验证码。',
      otpResendCode: '重新发送验证码',
      terms:
        '继续即表示你同意我们的 <terms>服务条款</terms>、<privacy>隐私政策</privacy> 和 <dpa>数据处理协议</dpa>。',
      noAccount: '还没有账户？',
      signUpCta: '注册 →',
      haveAccount: '已经有账户？',
      signInCta: '登录 →',
      errors: {
        ...(resources.en.translation.signup?.errors || {}),
        invalidPhone: '请输入有效的电话号码。',
        numberAlreadyRegistered: '该号码已注册。请直接登录。',
        enterCode: '请输入收到的验证码。',
        invalidOtp: '验证码无效或已过期。',
        sendFailed: '发送失败，请重试。',
      },
    },
    qualify: {
      ...(resources.en.translation.qualify || {}),
      eyebrow: '资格检查',
      badge: '不到1分钟',
      title: '查看你是否 <accent>符合资格</accent>',
      subtitle: '回答几个是/否问题，判断你是否符合 2026 西班牙正规化途径。',
      yes: '是',
      no: '否',
      q1: '你是否在 2026 年 1 月 1 日之前到达西班牙？',
      q2: '你是否曾在西班牙申请过庇护？',
      q3: '你是否曾在西班牙被判犯有罪行？',
      q4: '你的犯罪记录是否已经清除？',
      q5: '你认为法定等待期是否已过，现在可以清除记录？',
      altTitle: '让我们探索 <accent>其他选择</accent>',
      altDesc:
        '根据你的回答，你可能不符合 2026 年特别正规化资格。不过，你可能仍有其他合法选择。继续注册后，专员将审查你的情况并确定最佳方案。',
      continue: '继续',
      startOver: '重新开始',
      qualifiedPrivacy:
        '你的答案将保密，并受我们的 <privacy>隐私政策</privacy> 保护。',
      qualifiedBadge: '资格已确认',
      qualifiedTitle: '好消息--你符合 <accent>2026 特别正规化</accent> 条件。',
      qualifiedSub: '我们继续。团队会全程指导你--从材料到最终提交。',
    },
    welcomePage: {
      ...(resources.en.translation.welcomePage || {}),
      welcomeBadge: '欢迎回来',
      title: '你的门户已准备就绪',
      body:
        '欢迎回来。你的安全客户门户已就绪，你可以按自己的节奏完成所有步骤，并通过手机上传文件。这会加快你的案件进度并减少错误。点击下方继续。',
      next1: '你的信息已安全存储在系统中',
      next2: '专员正在审核你的案件',
      next3: '我们将通过电话、WhatsApp 或邮箱与你联系',
      next4: '如果已超过 48 小时，请给我们留言--我们会尽快回复',
      footnote: '我们只会就你的案件与你联系',
      primaryCta: '通过 WhatsApp 联系我们',
      backLink: '返回首页',
    },
    ssoSignup: {
      ...(resources.en.translation.ssoSignup || {}),
      newAccount: '新账户',
      step2of2: '第 2 步（共 2 步）',
      step3of3: '第 3 步（共 3 步）',
      takesLessThanMinute: '不到 1 分钟',
      almostDone: '快完成了',
      detailsTitle: '<accent>我们开始吧</accent>',
      detailsTitlePlain: '我们开始吧',
      detailsSubtitle: '只需一些快速信息',
      detailsContinueTerms:
        '继续即表示你同意我们的 <terms>服务条款</terms>、<privacy>隐私政策</privacy> 和 <dpa>数据处理协议</dpa>。',
      firstName: '名',
      lastName: '姓',
      forQuestion: '这份申请是给谁的？',
      forMe: '给我自己',
      forSomeoneElse: '给他人',
      familyQuestion: '你是单独申请还是家庭申请？',
      justMe: '仅我一人',
      forMyFamily: '为我的家庭',
      howManyPeople: '多少人？',
      includingYourself: '包含你自己',
      continue: '继续',
      noDocsNeededShort: '此阶段无需提供文件。',
      guideEverythingShort: '我们会全程指导你。',
      contactTitle: '我们应该如何<accent>联系你？</accent>',
      contactSubtitle: '我们会用这些信息发送与你案件相关的更新',
      phonePreferred: '你的电话（首选）',
      contactBy: '我们将通过 WhatsApp、短信或邮箱联系你',
      sendCode: '发送验证码',
      resend: '重新发送',
      verified: '已验证',
      enterCode: '输入 6 位验证码',
      incorrectCode: '验证码错误，请重试。',
      resendCode: '重新发送验证码',
      inSeconds: '{{seconds}} 秒后',
      verifyEmailOptional: '你的邮箱',
      optional: '（可选）',
      updatesOrDocs: '我们可能会在这里发送重要更新或文件',
      emailAsMain: '使用邮箱作为主要联系方式（替代手机）',
      continueNextSteps: '继续下一步',
      saving: '保存中…',
      errors: {
        nameRequired: '请填写名和姓。',
        forRequired: '请选择这份申请是给谁的。',
        familyRequired: '请选择你是单独申请还是家庭申请。',
        saveFailed: '保存失败，请重试。',
      },
      onlyCaseContact: '我们只会就你的案件联系你',
    },
    successPage: {
      ...(resources.en.translation.successPage || {}),
      brandTitle: 'Global Mobility',
      brandSubtitle: '西班牙正规化 2026',
      completedBadge: '注册已完成',
      title: '一切就绪！',
      body: '我们将在 24-48 小时内联系你安排下一步。',
      next1: '我们审核你的信息',
      next2: '我们通过电话或邮箱联系你',
      next3: '我们指导你完成后续步骤',
      next4: '你可以开始准备：护照、在西班牙的时间证明以及支持材料',
      referral: {
        ...(resources.en.translation.successPage?.referral || {}),
        ariaLabel: '你有推荐码吗？',
        title: '你有推荐码吗？',
        subtitle: '可选 - 如果有人推荐了你，请在这里输入他们的代码',
        apply: '应用',
        applying: '应用中...',
        applied: '已应用',
        appliedMessage: '推荐码应用成功。',
        applyError: '无法应用推荐码。',
      },
      backHome: '返回顶部',
      footer: '我们只会就你的案件与你联系',
      companyLine1: 'Global Mobility 是以下公司的商号：',
      companyName: 'Global Mobility Group LLC',
      companyAddress: '140 E Broadway Ave, Suite 25, Jackson, WY 83001, USA',
      companyLine2: '欧盟代表：Ana Maria Travieso Hernandez -',
      linkTerms: '服务条款',
      linkPrivacy: '隐私政策',
      linkCookies: 'Cookie（曲奇）政策',
      linkLegal: '法律声明',
      copyright: '© 2026 Global Mobility Group LLC',
    },
  },
};

resources.fr.translation.advisorPortal = advisorPortalFr;
resources.ar.translation.advisorPortal = advisorPortalAr;
resources.zh.translation.advisorPortal = advisorPortalZh;

function normalizeLang(value) {
  if (!value) return '';
  return String(value).trim().toLowerCase().replace('_', '-').split('-')[0];
}

function safeLocalStorageGet(key) {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeLocalStorageSet(key, value) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // ignore storage failures (private mode, blocked, etc.)
  }
}

function getBrowserLangCandidates() {
  if (typeof navigator === 'undefined') return [];
  const langs = Array.isArray(navigator.languages) ? navigator.languages : [];
  const first = navigator.language ? [navigator.language] : [];
  return [...langs, ...first].filter(Boolean);
}

function resolveInitialLanguage({ supported, fallback }) {
  const supportedSet = new Set(supported.map(normalizeLang));
  const fallbackBase = normalizeLang(fallback) || 'en';

  // 1) Prioritize localStorage (explicit user choice from language switch)
  const stored = normalizeLang(safeLocalStorageGet(STORAGE_KEY));
  if (stored && supportedSet.has(stored)) return stored;

  // 2) App default (English) before browser detection so LP UI matches reference
  if (supportedSet.has(fallbackBase)) {
    safeLocalStorageSet(STORAGE_KEY, fallbackBase);
    return fallbackBase;
  }

  // 3) Detect from browser language(s)
  for (const candidate of getBrowserLangCandidates()) {
    const base = normalizeLang(candidate);
    if (base && supportedSet.has(base)) {
      safeLocalStorageSet(STORAGE_KEY, base);
      return base;
    }
  }

  safeLocalStorageSet(STORAGE_KEY, fallbackBase);
  return fallbackBase;
}

const SUPPORTED_LANGS = Object.keys(resources);
const initialLanguage = resolveInitialLanguage({
  supported: SUPPORTED_LANGS,
  fallback: I18N_CONFIG.defaultLanguage || 'en',
});

i18n
  .use(initReactI18next)
  .init({
    resources,
    lng: initialLanguage,
    fallbackLng: ['en', 'es'],
    interpolation: { escapeValue: false },
  });

function syncDocumentLang(lng) {
  if (typeof document === 'undefined') return;
  const base = String(lng || I18N_CONFIG.defaultLanguage).split('-')[0];
  try {
    document.documentElement.lang = base;
    document.documentElement.dir = RTL_LANGS.has(base) ? 'rtl' : 'ltr';
  } catch {
    // ignore DOM write failures
  }
}

syncDocumentLang(i18n.resolvedLanguage || i18n.language);
i18n.on('languageChanged', syncDocumentLang);

export default i18n;
